import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useDispatch, useSelector } from 'react-redux';
import { updateStudent as updateStudentAction, fetchMasterData } from '../../store/slices/masterDataSlice';
import styled from 'styled-components';
import axios from 'axios';
import { FaEye, FaPaperPlane, FaDownload, FaSearch, FaCog, FaList, FaTrash, FaTimes, FaUpload, FaSync, FaPencilAlt, FaRegIdCard, FaRegEdit, FaPlus, FaArrowRight } from 'react-icons/fa';
import { format, addDays } from 'date-fns';
import { getStudentDetails, deleteStudent, uploadParticular, getStudentForEdit, updateParticular, uploadStudentDocument, deleteParticular, deleteParticularFile, fetchStudentTransferHistory, assignCareerCoachToStudent, bulkUpdateStudentStatus } from '../../utils/studentsApi';
import { fetchCareerCoaches } from '../../utils/careerCoachesApi';
import { CourseSelect, SessionSelect } from '../../components/MasterDataSelect';
import { showSuccess, showError } from '../../components/CustomAlert';
import { useConfirm } from '../../components/useConfirm';
import CustomConfirm, { showDeleteConfirm as showDeleteConfirmStatic } from '../../components/CustomConfirm';
import { getSecureFileUrl, getSecureFileUrlWithToken } from '../../utils/secureFileAccess';
import { API_BASE_URL, getAuthHeaders } from '../../utils/apiConfig';
import { hasPermission } from '../../utils/permissions';

function getStudentParticularPaths(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw.filter((p) => typeof p === 'string' && p.length > 0);
  if (typeof raw === 'string') return [raw];
  return [];
}

const Container = styled.div`
  padding: 30px;
  min-height: 100vh;
`;

const FilterSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 200px;
`;

const Label = styled.label`
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 0.9rem;
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);;
  }
`;

const SearchButton = styled.button`
   border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  transition: transform 0.2s ease;
  background: transparent;
  border: 1px solid #e9ecef;
  color: #2f2e2e;
  margin-top: 24px;

  &:hover {
    transform: translateY(-2px);
  }
`;

const SearchInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }
`;

const RefreshButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: rgb(11, 23, 92);
    transform: translateY(-1px);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ExportButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #218838;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const UpdateStatusButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: rgb(33, 41, 90);
    transform: translateY(-1px);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ExportModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  padding: 20px;
`;

const ExportModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 520px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const ExportModalTitle = styled.h3`
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
`;

const ExportModalSubtitle = styled.p`
  margin: 0 0 20px 0;
  color: #6c757d;
  font-size: 0.9rem;
`;

const ExportModalField = styled.div`
  margin-bottom: 16px;
`;

const ExportModalFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  margin-top: 8px;
  border-top: 1px solid #e9ecef;
`;

const ExportModalCancelButton = styled.button`
  background: white;
  color: #495057;
  border: 1px solid #dee2e6;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;

  &:hover {
    background: #f8f9fa;
  }
`;

const ExportModalSubmitButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #218838;
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ExportStudentListSection = styled.div`
  margin-bottom: 8px;
`;

const ExportStudentListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
`;

const ExportStudentListSummary = styled.span`
  font-size: 0.9rem;
  color: #495057;
  font-weight: 600;
`;

const ExportStudentListActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ExportStudentListActionLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: rgb(51, 66, 147);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: rgb(33, 41, 90);
  }

  &:disabled {
    color: #adb5bd;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const ExportStudentList = styled.div`
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #f8f9fa;
`;

const ExportStudentListItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.9rem;
  color: #2c3e50;
  background: white;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8f9fa;
  }

  input {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const ExportStudentListEmpty = styled.div`
  padding: 16px;
  text-align: center;
  color: #6c757d;
  font-size: 0.9rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
`;

const ExportStudentListMeta = styled.span`
  color: #6c757d;
  font-size: 0.8rem;
  margin-left: auto;
  flex-shrink: 0;
`;

const StudentListContainer = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const ListHeader = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
`;

const BreadcrumbNav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 15px;
`;

const BreadcrumbItem = styled.span`
  &:after {
    content: '>';
    margin-left: 8px;
  }
  
  &:last-child:after {
    content: '';
  }
`;

const ListTitle = styled.h2`
  color: #2c3e50;
  margin: 0;
  font-size: 1.5rem;
`;

const TableControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e9ecef;
`;

const ControlsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ControlsRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IconButton = styled.button`
  background: transparent;
  border: 1px solid #e9ecef;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  color: #6c757d;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #f8f9fa;
`;

const Th = styled.th`
  padding: 15px 20px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #e9ecef;
  font-size: 0.9rem;
`;

const SortableHeader = styled(Th)`
  cursor: pointer;
  user-select: none;
  text-align: left;
  &:hover {
    background: #e9ecef;
  }
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
&:hover {
    background: #f8f9fa;

  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #e9ecef;
  }
`;

const Td = styled.td`
  padding: 15px 20px;
  color: #495057;
  font-size: 0.95rem;
`;

const CheckboxInput = styled.input`
  transform: scale(1.2);
`;

const ProfileImageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProfileImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e9ecef;
  background: #f8f9fa;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const DefaultAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgb(51, 66, 147);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  border: 2px solid #e9ecef;
`;

const ImageModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  cursor: pointer;
`;

const ExpandedImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  cursor: default;
`;

const ImageModalClose = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  color: #333;
  transition: all 0.2s ease;
  z-index: 2001;
  
  &:hover {
    background: white;
    transform: scale(1.1);
  }
`;

const ViewDetailsButton = styled.button`
  background: transparent;
  border: none;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  svg {
    font-size: 1.2rem;
  }
  
  &:hover {
    background: #f8f9fa;
    color: rgb(51, 66, 147);
    transform: scale(1.1);
  }
`;

const ActionButton = styled.button`
  border: 1px solid rgb(52 152 219);
  color: rgb(52 152 219);
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  margin-right: 8px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  ${props => props.action === 'delete' && `
    border-color: #dc3545;
    color: #dc3545;
    
    &:hover {
      background: #dc3545;
      color: white;
    }
  `}
`;

const StudentStatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;

  ${(props) => {
    switch (normalizeStudentStatus(props.status)) {
      case 'active':
        return `
          background: #d4edda;
          color: #155724;
        `;
      case 'graduated':
        return `
          background: #cce5ff;
          color: #004085;
        `;
      case 'inactive':
        return `
          background: #e9ecef;
          color: #495057;
        `;
      case 'withdrawn':
        return `
          background: #fff3cd;
          color: #856404;
        `;
      default:
        return `
          background: #f8f9fa;
          color: #6c757d;
        `;
    }
  }}
`;

const AttendanceStatus = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
  
  ${props => {
    switch (props.status?.toLowerCase()) {
      case 'absent':
        return `
          background: #f8d7da;
          color: #721c24;
        `;
      case 'on leave':
      case 'leave':
        return `
          background: #cce5ff;
          color: #004085;
        `;
      case 'present':
        return `
          background: #d4edda;
          color: #155724;
        `;
      default:
        return `
          background: #f8f9fa;
          color: #6c757d;
        `;
    }
  }}
`;

const Pagination = styled.div`
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e9ecef;
`;

const ShowingText = styled.span`
  color: #6c757d;
  font-size: 0.9rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  max-width: 1200px;
  width: 100vw;
  max-height: 100vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  margin: 0;
  position: relative;
  padding: 0;
`;

const ModalHeader = styled.div`
  position: sticky;
  top: 0;
  background: #fff;
  border-radius: 15px 15px 0 0;
  border-bottom: 1px solid #e9ecef;
  padding: 30px 40px 18px 40px;
  display: flex;
  align-items: center;
  z-index: 2;
`;

const ModalTitle = styled.h2`
  color: #2c3e50;
  margin: 0;
  font-size: 1.35rem;
  font-weight: 600;
  flex: 1;
`;

const ModalClose = styled.button`
  background: none;
  border: none;
  font-size: 2.1rem;
  color: #b0b0b0;
  cursor: pointer;
  margin-left: 18px;
  margin-top: 0;
  transition: color 0.2s;
  &:hover {
    color: #333;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #6c757d;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  
  &:hover {
    background: #e9ecef;
    color: #2c3e50;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 1.08rem;
  color: #334;
  font-weight: 600;
  padding: 32px 0 8px 0;
  border-top: 1px solid #e9ecef;
`;

const DetailsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0;
  font-size: 0.98rem;
  background: #fff;
  th, td {
    padding: 10px 18px;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: top;
  }
  th {
    color: #555;
    font-weight: 500;
    background: #f8f9fa;
    width: 260px;
    border-right: 1px solid #f0f0f0;
  }
  tr:last-child td, tr:last-child th {
    border-bottom: none;
  }
`;

const OverviewTransferFlow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const OverviewTransferCourseTitle = styled.div`
  font-weight: 600;
  color: #2c3e50;
`;

const OverviewTransferSessionLine = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
`;

const OverviewTransferArrow = styled(FaArrowRight)`
  color: #007bff;
  font-size: 1.1rem;
  flex-shrink: 0;
`;

const TransferReasonBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  background: ${(props) => {
    switch (props.$type) {
      case 'Course Completion':
        return '#d4edda';
      case 'Schedule Change':
        return '#fff3cd';
      case 'Student Request':
        return '#cce5ff';
      case 'Administrative':
        return '#e2e3e5';
      default:
        return '#f8d7da';
    }
  }};
  color: ${(props) => {
    switch (props.$type) {
      case 'Course Completion':
        return '#155724';
      case 'Schedule Change':
        return '#856404';
      case 'Student Request':
        return '#004085';
      case 'Administrative':
        return '#383d41';
      default:
        return '#721c24';
    }
  }};
`;

const FileLink = styled.a`
  color: #3366cc;
  text-decoration: underline;
  font-size: 0.97rem;
`;

const UploadButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #218838;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CareerCoachValueText = styled.span`
  flex: 1;
  min-width: 0;
  color: inherit;
  font-size: inherit;
  line-height: inherit;
`;

const CareerCoachSelect = styled.select`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: inherit;
  font-size: inherit;
  font-family: inherit;
  line-height: inherit;
  padding: 0;
  margin: 0;
  cursor: pointer;
  box-shadow: none;
  appearance: auto;

  &:focus {
    outline: none;
  }
`;

const CareerCoachFieldRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
`;

const CareerCoachCancelButton = styled.button`
  background: transparent;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
  line-height: 1;

  &:hover {
    color: #dc3545;
  }
`;

// Signature pad styled components
const SignaturePadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SignatureCanvas = styled.canvas`
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: crosshair;
  background: #f8f9fa;
  width: 300px;
  height: 150px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
  }
`;

const ClearSignatureButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  align-self: flex-start;
  
  &:hover {
    background: #545b62;
  }
`;

const SignatureButton = styled.button`
  background: none;
  border: none;
  color: #3366cc;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.97rem;
  padding: 0;
  margin-left: 10px;
  
  &:hover {
    color: #0056b3;
  }
`;

// Particulars Upload Modal Styles
const ParticularsModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 20px;
`;

const ParticularsModalContent = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ParticularsModalHeader = styled.div`
  padding: 25px 30px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  border-radius: 15px 15px 0 0;
`;

const ParticularsModalTitle = styled.h2`
  color: #2c3e50;
  margin: 0;
  font-size: 1.4rem;
  font-weight: 600;
`;

const ParticularsModalBody = styled.div`
  padding: 30px;
`;

const ParticularsFormGroup = styled.div`
  margin-bottom: 20px;
`;

const ParticularsLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.9rem;
`;

const ParticularsInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ParticularsButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
`;

const ParticularsSubmitButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ParticularsCancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    background: #5a6268;
    transform: translateY(-1px);
  }
`;

const GlobalStyle = styled.div`
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ParticularsSection = styled.div`
  margin-top: 20px;
`;

const ParticularsSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 4px;
`;

const AddParticularButtonWrap = styled.div`
  position: relative;
`;

const AddParticularButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: rgb(11, 23, 92);
    box-shadow: 0 4px 12px rgba(51, 66, 147, 0.3);
  }
`;

const AddParticularDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 6px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 100;
  min-width: 220px;
  max-height: 280px;
  overflow-y: auto;
`;

const AddParticularDropdownItem = styled.button`
  display: block;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: none;
  text-align: left;
  font-size: 0.9rem;
  color: #2c3e50;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f0f4ff;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const ParticularsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
  margin-top: 15px;
`;

const ParticularsItem = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 15px;
  background: #f8f9fa;
  position: relative;
`;

const ParticularsItemTitle = styled.h4`
  margin: 0 0 10px 0;
  color: #2c3e50;
  font-size: 0.9rem;
  font-weight: 600;
  padding-right: 100px;
`;

const ParticularsAddButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
`;

const ParticularsFileLink = styled.a`
  color: #3366cc;
  text-decoration: underline;
  font-size: 0.85rem;
  display: block;
  margin-top: 8px;
`;

const ParticularsFileLinksWrap = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ParticularsSmallRemoveBtn = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #c82333;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ParticularsExistingFilesBox = styled.div`
  margin-bottom: 20px;
  padding: 12px 14px;
  background: #f1f3f5;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const ParticularsExistingFileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 0.85rem;
  margin-top: 8px;

  &:first-child {
    margin-top: 0;
  }
`;

const ParticularsUpdateButton = styled.button`
  background: #4CAF50;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  position: absolute;
  top: 15px;
  right: 15px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  }
`;

const ParticularsDeleteButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: #c82333;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(220, 53, 69, 0.3);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

// Edit Modal Styles
const EditModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 20px;
`;

const EditModalContent = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
`;

const EditModalHeader = styled.div`
  padding: 25px 30px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  border-radius: 15px 15px 0 0;
`;

const EditModalTitle = styled.h2`
  color: #2c3e50;
  margin: 0;
  font-size: 1.4rem;
  font-weight: 600;
`;

const EditModalBody = styled.div`
  padding: 30px;
`;

const EditForm = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const EditFormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const EditLabel = styled.label`
  margin-bottom: 8px;
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.9rem;
`;

const EditInput = styled.input`
  padding: 12px 15px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const EditSelect = styled.select`
  padding: 12px 15px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  background: white;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const EditTextarea = styled.textarea`
  padding: 12px 15px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const EditButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: space-between;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
  grid-column: 1 / -1;
`;

const EditLeftButtons = styled.div`
  display: flex;
  gap: 15px;
`;

const EditRightButtons = styled.div`
  display: flex;
  gap: 15px;
`;

const DeleteButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    background: #c82333;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(220, 53, 69, 0.3);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const EditSubmitButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const EditCancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    background: #5a6268;
    transform: translateY(-1px);
  }
`;

function StudentEditModal({ student, onClose, onUpdate, canUpdateStudent, canDeleteStudent }) {
  const dispatch = useDispatch();
  const [careerCoaches, setCareerCoaches] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    gender: '',
    dateOfBirth: '',
    address: '',
    workforceCenter: '',
    careerCoachId: '',
    emergencyContact: '',
    emergencyContactPhone: '',
    otherEmergencyContact: '',
    nationality: '',
    socialSecurityNumber: '',
    studentNotes: '',
    status: 'Active'
  });
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCareerCoaches({ activeOnly: true })
      .then(setCareerCoaches)
      .catch(() => setCareerCoaches([]));
  }, []);

  // Fetch complete student data when student prop changes
  useEffect(() => {
    const fetchStudentData = async () => {
      if (!student?.id) return;

      try {
        setFetchingData(true);
        const response = await getStudentForEdit(student.id);

        if (response.success && response.data) {
          setFormData({
            firstName: response.data.firstName || '',
            lastName: response.data.lastName || '',
            email: response.data.email || '',
            phone: response.data.phone || '',
            gender: response.data.gender || '',
            dateOfBirth: response.data.dateOfBirth || '',
            address: response.data.address || '',
            workforceCenter: response.data.workforceCenter || '',
            careerCoachId: response.data.careerCoachId ? String(response.data.careerCoachId) : '',
            emergencyContact: response.data.emergencyContact || '',
            emergencyContactPhone: response.data.emergencyContactPhone || '',
            otherEmergencyContact: response.data.otherEmergencyContact || '',
            nationality: response.data.nationality || '',
            socialSecurityNumber: response.data.socialSecurityNumber || '',
            studentNotes: response.data.studentNotes || '',
            status: formatStudentStatusLabel(response.data.status) === 'N/A'
              ? 'Active'
              : formatStudentStatusLabel(response.data.status)
          });
        } else {
          console.error('Failed to fetch student data:', response.message);
          showError('Failed to load student data');
        }
      } catch (error) {
        console.error('Error fetching student data:', error);
        showError('Error loading student data');
      } finally {
        setFetchingData(false);
      }
    };

    fetchStudentData();
  }, [student?.id]);

  const handleCareerCoachSelect = (coachId) => {
    const coach = careerCoaches.find((c) => String(c.id) === String(coachId));
    setFormData((prev) => ({
      ...prev,
      careerCoachId: coachId,
      workforceCenter: coach ? coach.name : ''
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { careerCoachId, workforceCenter, ...rest } = formData;
      await onUpdate(student.id, rest);
      const assignResult = await assignCareerCoachToStudent(
        student.id,
        careerCoachId ? parseInt(careerCoachId, 10) : null
      );
      const assigned = assignResult.data || {};
      dispatch(
        updateStudentAction({
          studentId: student.id,
          updatedData: {
            careerCoachId: assigned.careerCoachId,
            workforceCenter: assigned.workforceCenter || ''
          }
        })
      );
      onClose();
    } catch (error) {
      console.error('Error updating student:', error);
      showError(error.message || 'Error updating student');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteStudent(student.id);
      showSuccess('Student deleted successfully');

      // Update Redux state to remove the deleted student
      dispatch({
        type: 'masterData/removeStudent',
        payload: student.id
      });

      onClose();
    } catch (error) {
      console.error('Error deleting student:', error);
      showError('Failed to delete student');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  if (fetchingData) {
    return (
      <EditModalOverlay onClick={onClose}>
        <EditModalContent onClick={(e) => e.stopPropagation()}>
          <EditModalHeader>
            <EditModalTitle>Loading Student Data...</EditModalTitle>
            <CloseButton onClick={onClose}>
              <FaTimes />
            </CloseButton>
          </EditModalHeader>

          <EditModalBody>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <FaSync style={{ animation: 'spin 1s linear infinite', fontSize: '2rem', color: '#6c757d' }} />
              <p style={{ marginTop: '20px', color: '#6c757d' }}>Loading student information...</p>
            </div>
          </EditModalBody>
        </EditModalContent>
      </EditModalOverlay>
    );
  }

  return (
    <EditModalOverlay onClick={onClose}>
      <EditModalContent onClick={(e) => e.stopPropagation()}>
        <EditModalHeader>
          <EditModalTitle>Edit Student - {student?.firstName} {student?.lastName}</EditModalTitle>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
        </EditModalHeader>

        <EditModalBody>
          <EditForm onSubmit={handleSubmit}>
            <EditFormGroup>
              <EditLabel>First Name *</EditLabel>
              <EditInput
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
              />
            </EditFormGroup>

            <EditFormGroup>
              <EditLabel>Last Name *</EditLabel>
              <EditInput
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
              />
            </EditFormGroup>

            <EditFormGroup>
              <EditLabel>Email Address *</EditLabel>
              <EditInput
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
              />
            </EditFormGroup>

            <EditFormGroup>
              <EditLabel>Phone Number</EditLabel>
              <EditInput
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </EditFormGroup>

            <EditFormGroup>
              <EditLabel>Gender</EditLabel>
              <EditSelect
                value={formData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </EditSelect>
            </EditFormGroup>

            <EditFormGroup>
              <EditLabel>Date of Birth</EditLabel>
              <EditInput
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
              />
            </EditFormGroup>

            <EditFormGroup>
              <EditLabel>Address</EditLabel>
              <EditInput
                type="text"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
              />
            </EditFormGroup>

            <EditFormGroup>
              <EditLabel>Emergency Contact</EditLabel>
              <EditInput
                type="text"
                value={formData.emergencyContact}
                onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
              />
            </EditFormGroup>

            <EditFormGroup>
              <EditLabel>Emergency Contact Phone</EditLabel>
              <EditInput
                type="tel"
                value={formData.emergencyContactPhone}
                onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
              />
            </EditFormGroup>

            <EditFormGroup>
              <EditLabel>Other Emergency Contact</EditLabel>
              <EditInput
                type="text"
                value={formData.otherEmergencyContact}
                onChange={(e) => handleInputChange('otherEmergencyContact', e.target.value)}
              />
            </EditFormGroup>









            <EditFormGroup>
              <EditLabel>Career Coach</EditLabel>
              <Select
                value={formData.careerCoachId}
                onChange={(e) => handleCareerCoachSelect(e.target.value)}
              >
                <option value="">— None —</option>
                {careerCoaches.map((coach) => (
                  <option key={coach.id} value={String(coach.id)}>
                    {coach.name}
                  </option>
                ))}
              </Select>
            </EditFormGroup>

            <EditFormGroup>
              <EditLabel>Nationality</EditLabel>
              <EditInput
                type="text"
                value={formData.nationality}
                onChange={(e) => handleInputChange('nationality', e.target.value)}
              />
            </EditFormGroup>

            <EditFormGroup>
              <EditLabel>Social Security Number</EditLabel>
              <EditInput
                type="text"
                value={formData.socialSecurityNumber}
                onChange={(e) => handleInputChange('socialSecurityNumber', e.target.value)}
              />
            </EditFormGroup>

            <EditFormGroup>
              <EditLabel>Status</EditLabel>
              <EditSelect
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Graduated">Graduated</option>
                <option value="Withdrawn">Withdrawn</option>
              </EditSelect>
            </EditFormGroup>

            <EditFormGroup style={{ gridColumn: '1 / -1' }}>
              <EditLabel>Student Notes</EditLabel>
              <EditTextarea
                value={formData.studentNotes}
                onChange={(e) => handleInputChange('studentNotes', e.target.value)}
                placeholder="Enter any additional notes about the student..."
              />
            </EditFormGroup>

            <EditButtonGroup>
              <EditLeftButtons>
                {canDeleteStudent && (
                  <DeleteButton type="button" onClick={handleDeleteClick} disabled={deleting}>
                    {deleting ? 'Deleting...' : 'Delete Student'}
                  </DeleteButton>
                )}
              </EditLeftButtons>
              <EditRightButtons>
                <EditCancelButton type="button" onClick={onClose}>
                  Cancel
                </EditCancelButton>
                {canUpdateStudent && (
                  <EditSubmitButton type="submit" disabled={loading}>
                    {loading ? 'Updating...' : 'Update Student'}
                  </EditSubmitButton>
                )}
              </EditRightButtons>
            </EditButtonGroup>
          </EditForm>
        </EditModalBody>
      </EditModalContent>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <ParticularsModalOverlay onClick={handleDeleteCancel}>
          <ParticularsModalContent onClick={(e) => e.stopPropagation()}>
            <ParticularsModalHeader style={{ background: '#dc3545', color: 'white' }}>
              <ParticularsModalTitle style={{ color: 'white' }}>Confirm Delete Student</ParticularsModalTitle>
              <CloseButton onClick={handleDeleteCancel} style={{ color: 'white' }}>
                <FaTimes />
              </CloseButton>
            </ParticularsModalHeader>

            <ParticularsModalBody>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '1.2rem', marginBottom: '15px', color: '#dc3545' }}>
                  ⚠️ Warning: This action cannot be undone!
                </div>
                <p style={{ fontSize: '1rem', marginBottom: '20px', color: '#333' }}>
                  Are you sure you want to delete <strong>{student?.firstName} {student?.lastName}</strong>?
                </p>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '30px' }}>
                  This will permanently remove the student and all associated data from the system.
                </p>
              </div>

              <ParticularsButtonGroup>
                <ParticularsCancelButton onClick={handleDeleteCancel}>
                  Cancel
                </ParticularsCancelButton>
                <ParticularsSubmitButton
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                  style={{ background: '#dc3545', borderColor: '#dc3545' }}
                >
                  {deleting ? 'Deleting...' : 'Yes, Delete Student'}
                </ParticularsSubmitButton>
              </ParticularsButtonGroup>
            </ParticularsModalBody>
          </ParticularsModalContent>
        </ParticularsModalOverlay>
      )}
    </EditModalOverlay>
  );
}

const detailsCacheByStudentId = {};
function buildFourWeekAttendanceRanges(startDate, hardEndDate) {
  const ranges = [];
  let cursor = new Date(startDate);
  const today = new Date();
  const providedEnd = hardEndDate ? new Date(hardEndDate) : null;
  const maxEnd = providedEnd && providedEnd < today ? providedEnd : today;
  while (cursor <= maxEnd) {
    const startDow = cursor.getDay();
    const firstWeekEndOffset = (5 - startDow + 7) % 7;
    const firstWeekEnd = addDays(cursor, firstWeekEndOffset);
    const firstMondayAfter = addDays(firstWeekEnd, 3);
    const fourthFriday = addDays(firstMondayAfter, 18);
    const naturalEnd = fourthFriday;
    const end = new Date(Math.min(naturalEnd.getTime(), maxEnd.getTime()));
    const labelStart = format(cursor, 'MM/dd/yy');
    const labelEnd = format(end, 'MM/dd/yy');
    const urlStart = format(cursor, 'yyyy-MM-dd');
    const urlEnd = format(end, 'yyyy-MM-dd');
    ranges.push({ labelStart, labelEnd, urlStart, urlEnd });
    cursor = addDays(end, 1);
  }
  return ranges;
}

function StudentDetailsModal({ student, onClose, buildFileUrl, onCareerCoachAssigned, canUpdateStudent }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [careerCoaches, setCareerCoaches] = useState([]);
  const [selectedCareerCoachId, setSelectedCareerCoachId] = useState('');
  const [careerCoachEditMode, setCareerCoachEditMode] = useState(false);
  const [savingCareerCoach, setSavingCareerCoach] = useState(false);
  const [showParticularsModal, setShowParticularsModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedParticular, setSelectedParticular] = useState(null);
  const [uploadingParticular, setUploadingParticular] = useState(false);
  const [updatingParticular, setUpdatingParticular] = useState(false);
  const [selectedParticularFiles, setSelectedParticularFiles] = useState([]);
  const [showDeleteParticularModal, setShowDeleteParticularModal] = useState(false);
  const [deletingParticular, setDeletingParticular] = useState(false);
  const [removingParticularFilePath, setRemovingParticularFilePath] = useState(null);
  const [showAddParticularDropdown, setShowAddParticularDropdown] = useState(false);
  const addParticularDropdownRef = useRef(null);

  const [transferHistory, setTransferHistory] = useState([]);
  const [transferHistoryLoading, setTransferHistoryLoading] = useState(false);
  const [attendanceSheetPeriod, setAttendanceSheetPeriod] = useState(undefined);
  const [attendanceSheetSessionId, setAttendanceSheetSessionId] = useState(null);
  const [attendanceSheetsLoading, setAttendanceSheetsLoading] = useState(false);

  // Document upload states
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [selectedDocumentFile, setSelectedDocumentFile] = useState(null);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  const [pendingDocumentUpdate, setPendingDocumentUpdate] = useState(null);

  // Signature pad states
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);
  const signatureCanvasRef = useRef(null);
  const [signatureContext, setSignatureContext] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [studentSignature, setStudentSignature] = useState('');

  // Get particulars from Redux state
  const { particulars } = useSelector(state => state.masterData);
  const { token } = useSelector(state => state.auth);

  // Helper function to open signed documents with proper title
  const openSignedDocument = (route, documentName) => {
    const newWindow = window.open(route, '_blank');
    if (newWindow && details?.student) {
      const studentName = `${details.student.firstName || ''} ${details.student.lastName || ''}`.trim();
      const studentId = details.student.id || student.id || '';
      const sanitizedDocName = documentName.replace(/[^a-zA-Z0-9_\-\s]/g, '');
      const fileName = `${studentName}-${studentId}-${sanitizedDocName}`;
      
      // Set the title after a brief delay to ensure the page has loaded
      setTimeout(() => {
        try {
          newWindow.document.title = fileName;
        } catch (e) {
          // Error setting document title
        }
      }, 500);
    }
  };

  // Handle viewing files with proper error handling
  const handleViewFile = async (filePath, fileType, documentName = null) => {
    try {
      if (!filePath) {
        showError('No file path provided');
        return;
      }

      const validatedPath = validateFilePath(filePath);
      const fileExtension = validatedPath.split('.').pop()?.toLowerCase();
      
      // For PDFs, open directly like SSN files
      if (fileExtension === 'pdf') {
        const secureUrl = buildFileUrl(filePath);
        window.open(secureUrl, '_blank');
        return;
      }
      
      // For images and other files, use the custom viewer
      const secureUrl = `${API_BASE_URL}/files/${validatedPath}`;
    
      
      // Make the request through axios so interceptors can handle token errors
      const response = await axios.get(secureUrl, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });


      // Determine MIME type from response headers or file extension
      let mimeType = response.headers['content-type'] || response.data.type;
      
      // If no MIME type from response, try to determine from file extension
      if (!mimeType) {
        switch (fileExtension) {
          case 'jpg':
          case 'jpeg':
            mimeType = 'image/jpeg';
            break;
          case 'png':
            mimeType = 'image/png';
            break;
          case 'gif':
            mimeType = 'image/gif';
            break;
          case 'pdf':
            mimeType = 'application/pdf';
            break;
          case 'doc':
            mimeType = 'application/msword';
            break;
          case 'docx':
            mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            break;
          default:
            mimeType = 'application/octet-stream';
        }
      }

      // Generate the filename
      let fileName = validatedPath.split('/').pop() || 'document';
      if (documentName && details?.student) {
        const studentName = `${details.student.firstName || ''} ${details.student.lastName || ''}`.trim();
        const studentId = details.student.id || student.id || '';
        const sanitizedDocName = documentName.replace(/[^a-zA-Z0-9_\-\s]/g, '');
        fileName = `${studentName}-${studentId}-${sanitizedDocName}.${fileExtension}`;
      }
      
      // Function to display the file viewer
      function displayFileViewer(fileDataUrl, isBlobUrl, fileMimeType) {
        // Open in a new window with HTML wrapper
        const newWindow = window.open('', '_blank');
        const isImageMime = fileMimeType.startsWith('image/');
        const imageToolbarTools = isImageMime
          ? `
                <div class="toolbar-tools" role="toolbar" aria-label="Image view">
                  <button type="button" class="tool-btn" onclick="imageViewerZoomOut()" title="Zoom out">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 13H5v-2h14v2z"/></svg>
                  </button>
                  <button type="button" class="tool-btn" onclick="imageViewerZoomIn()" title="Zoom in">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
                  </button>
                  <button type="button" class="tool-btn" onclick="imageViewerRotate()" title="Rotate clockwise">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8A5.99 5.99 0 0 1 6 12c0-3.31 2.69-6 6-6zm6.76 4.37l-1.77 1.13A6.02 6.02 0 0 1 18 12c0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z"/></svg>
                  </button>
                  <button type="button" class="tool-btn tool-btn-text" onclick="imageViewerReset()" title="Fit to window">Reset</button>
                </div>
              `
          : '';

        if (newWindow) {
          // Create HTML content with embedded viewer and download functionality
          const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
              <title>${fileName}</title>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <style>
                * {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
                }
                html, body {
                  width: 100%;
                  height: 100%;
                  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                  background: #525659;
                  overflow: hidden;
                }
                .toolbar {
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  height: 60px;
                  background: #323639;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                  display: flex;
                  align-items: center;
                  gap: 16px;
                  padding: 0 24px;
                  z-index: 1000;
                  color: #fff;
                }
                .toolbar-left {
                  display: flex;
                  align-items: center;
                  gap: 16px;
                  flex: 1;
                  min-width: 0;
                }
                .toolbar-tools {
                  display: flex;
                  align-items: center;
                  gap: 6px;
                  flex-shrink: 0;
                }
                .tool-btn {
                  background: #3c4043;
                  color: #e8eaed;
                  border: 1px solid #5f6368;
                  padding: 8px 10px;
                  border-radius: 4px;
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  transition: background 0.15s, border-color 0.15s;
                }
                .tool-btn:hover {
                  background: #5f6368;
                  border-color: #80868b;
                }
                .tool-btn svg {
                  width: 22px;
                  height: 22px;
                }
                .tool-btn-text {
                  font-size: 13px;
                  font-weight: 500;
                  padding: 8px 12px;
                }
                .filename {
                  font-size: 16px;
                  font-weight: 500;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  color: #fff;
                }
                .download-btn {
                  background: #1a73e8;
                  color: white;
                  border: none;
                  padding: 12px 28px;
                  border-radius: 4px;
                  cursor: pointer;
                  font-size: 15px;
                  font-weight: 500;
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  transition: all 0.2s;
                  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                  margin-left: auto;
                }
                .download-btn:hover {
                  background: #1557b0;
                  box-shadow: 0 2px 6px rgba(0,0,0,0.4);
                }
                .download-btn svg {
                  width: 18px;
                  height: 18px;
                }
                .viewer-container {
                  position: absolute;
                  top: 60px;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  overflow: hidden;
                  background: #525659;
                  padding: 0;
                }
                .image-viewer-scroll {
                  width: 100%;
                  height: 100%;
                  overflow: auto;
                  -webkit-overflow-scrolling: touch;
                }
                .image-viewer-stage {
                  min-width: 100%;
                  min-height: 100%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 32px;
                  box-sizing: border-box;
                }
                .image-tform {
                  flex-shrink: 0;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  overflow: visible;
                  box-shadow: 0 4px 20px rgba(0,0,0,0.5);
                }
                .viewer-img {
                  display: block;
                  max-width: none;
                  max-height: none;
                  user-select: none;
                  -webkit-user-drag: none;
                }
                .viewer {
                  display: block;
                  width: 100%;
                  height: 100%;
                  border: none;
                  box-shadow: 0 4px 20px rgba(0,0,0,0.5);
                }
                .viewer-pdf {
                  display: block;
                  width: 100%;
                  height: 100%;
                  border: none;
                  box-shadow: 0 4px 20px rgba(0,0,0,0.5);
                  max-width: 100%;
                  max-height: 100%;
                }
                .viewer-iframe {
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  border: none;
                  box-shadow: 0 4px 20px rgba(0,0,0,0.5);
                }
                .viewer-object {
                  display: block;
                  width: 100%;
                  height: 100%;
                  min-width: 100%;
                  min-height: 100%;
                  border: none;
                  box-shadow: 0 4px 20px rgba(0,0,0,0.5);
                }
                .viewer-object embed {
                  width: 100%;
                  height: 100%;
                  min-width: 100%;
                  min-height: 100%;
                }
              </style>
            </head>
            <body>
              <div class="toolbar">
                <div class="toolbar-left">
                  <div class="filename" title="${fileName}">${fileName}</div>
                </div>
                ${imageToolbarTools}
                <button class="download-btn" onclick="downloadFile()">
                  <svg fill="currentColor" viewBox="0 0 16 16">
                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                  </svg>
                  Download
                </button>
              </div>
              <div class="viewer-container">
                ${isImageMime
                  ? `<div class="image-viewer-scroll" id="image-viewer-scroll">
                  <div class="image-viewer-stage">
                    <div class="image-tform" id="image-tform">
                      <img class="viewer-img" id="viewer-img" src="${fileDataUrl}" alt="${fileName.replace(/"/g, '&quot;')}" />
                    </div>
                  </div>
                </div>`
                  : fileMimeType === 'application/pdf'
                  ? `<iframe class="viewer-iframe" src="${fileDataUrl}#zoom=page-width" style="width: 100%; height: 100%; border: none; position: absolute; top: 0; left: 0;"></iframe>`
                  : `<embed class="viewer" src="${fileDataUrl}" type="${fileMimeType}" style="width: 100%; height: 100%;" />`}
              </div>
              <script>
                const fileName = ${JSON.stringify(fileName)};
                const fileDataUrl = ${JSON.stringify(fileDataUrl)};
                const isBlobUrl = ${isBlobUrl};
                const fileMimeType = ${JSON.stringify(fileMimeType)};
                const isImageView = ${JSON.stringify(isImageMime)};
                
                function downloadFile() {
                  const link = document.createElement('a');
                  link.href = fileDataUrl;
                  link.download = fileName;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
                
                function imageViewerCenterScroll() {
                  var scroll = document.getElementById('image-viewer-scroll');
                  if (!scroll) return;
                  scroll.scrollLeft = Math.max(0, (scroll.scrollWidth - scroll.clientWidth) / 2);
                  scroll.scrollTop = Math.max(0, (scroll.scrollHeight - scroll.clientHeight) / 2);
                }
                
                if (isImageView) {
                  (function () {
                    var img = document.getElementById('viewer-img');
                    var scroll = document.getElementById('image-viewer-scroll');
                    var tform = document.getElementById('image-tform');
                    var rotation = 0;
                    var scale = 1;
                    var MIN_SCALE = 0.25;
                    var MAX_SCALE = 6;
                    var ZOOM_STEP = 1.2;
                    
                    function updateImageLayout() {
                      if (!img || !tform || !img.naturalWidth) return;
                      var w = img.naturalWidth;
                      var h = img.naturalHeight;
                      var rad = rotation * Math.PI / 180;
                      var c = Math.abs(Math.cos(rad));
                      var s = Math.abs(Math.sin(rad));
                      var boundW = Math.ceil((w * c + h * s) * scale);
                      var boundH = Math.ceil((w * s + h * c) * scale);
                      tform.style.width = boundW + 'px';
                      tform.style.height = boundH + 'px';
                      img.style.width = w + 'px';
                      img.style.height = h + 'px';
                      img.style.transform = 'rotate(' + rotation + 'deg) scale(' + scale + ')';
                      img.style.transformOrigin = 'center center';
                    }
                    
                    function fitToWindow() {
                      if (!img || !scroll || !img.naturalWidth) return;
                      rotation = 0;
                      var pad = 80;
                      var vw = Math.max(100, scroll.clientWidth - pad);
                      var vh = Math.max(100, scroll.clientHeight - pad);
                      var w = img.naturalWidth;
                      var h = img.naturalHeight;
                      scale = Math.min(vw / w, vh / h, 1);
                      if (!isFinite(scale) || scale <= 0) scale = 1;
                      scale = Math.max(scale, 0.05);
                      updateImageLayout();
                      imageViewerCenterScroll();
                    }
                    
                    window.imageViewerZoomIn = function () {
                      scale = Math.min(scale * ZOOM_STEP, MAX_SCALE);
                      updateImageLayout();
                    };
                    window.imageViewerZoomOut = function () {
                      scale = Math.max(scale / ZOOM_STEP, MIN_SCALE);
                      updateImageLayout();
                    };
                    window.imageViewerRotate = function () {
                      rotation = (rotation + 90) % 360;
                      updateImageLayout();
                      imageViewerCenterScroll();
                    };
                    window.imageViewerReset = function () {
                      fitToWindow();
                    };
                    
                    img.addEventListener('load', fitToWindow);
                    if (img.complete && img.naturalWidth) fitToWindow();
                    window.addEventListener('resize', function () {
                      if (rotation === 0 && scale <= 1) fitToWindow();
                    });
                  })();
                }
                
                // Clean up blob URL when window is closed
                if (isBlobUrl) {
                  window.addEventListener('beforeunload', function() {
                    URL.revokeObjectURL(fileDataUrl);
                  });
                }
              </script>
            </body>
            </html>
          `;
          
          newWindow.document.open();
          newWindow.document.write(htmlContent);
          newWindow.document.close();
        }
      }
      
      // Convert to base64 for images
      const reader = new FileReader();
      reader.onloadend = function() {
        const base64data = reader.result;
        displayFileViewer(base64data, false, mimeType);
      };
      const blob = new Blob([response.data], { type: mimeType });
      reader.readAsDataURL(blob);

    } catch (error) {
      console.error('Error viewing file:', error);
      console.error('Error response:', error.response);
      // The axios interceptor will handle token errors automatically
      if (error.response?.status !== 401) {
        showError('Failed to load file. Please try again.');
      }
    }
  };

  // Function to validate file path
  const validateFilePath = (filePath) => {
    if (!filePath) return '';
    
    // Remove the /home/cclcusa/public_html/ prefix if it exists
    if (filePath.startsWith('/home/cclcusa/public_html/')) {
      const cleanedPath = filePath.substring('/home/cclcusa/public_html/'.length);
      
      // Now remove admin/backend/uploads/ if it still exists
      if (cleanedPath.includes('admin/backend/uploads/')) {
        const finalPath = cleanedPath.replace('admin/backend/uploads/', '');
        return finalPath;
      }
      
      return cleanedPath;
    }
    
    // Remove the /opt/render/project/src/ prefix if it exists
    if (filePath.startsWith('/opt/render/project/src/')) {
      const cleanedPath = filePath.substring('/opt/render/project/src/'.length);
      
      // Now remove admin/backend/uploads/ if it still exists
      if (cleanedPath.includes('admin/backend/uploads/')) {
        const finalPath = cleanedPath.replace('admin/backend/uploads/', '');
        return finalPath;
      }
      
      return cleanedPath;
    }
    
    // If the path contains 'uploads/', extract only the part after it
    if (filePath.includes('uploads/')) {
      const uploadsIndex = filePath.indexOf('uploads/');
      return filePath.substring(uploadsIndex + 'uploads/'.length);
    }

    return filePath;
  };

  useEffect(() => {
    if (!student?.id) return;

    const cached = detailsCacheByStudentId[student.id];
    if (cached) {
      setDetails(cached);
      setLoading(false);
      setError(null);
    } else {
      setLoading(true);
      setError(null);
    }

    const fetchStudentDetails = async () => {
      try {
        const response = await getStudentDetails(student.id);
        const data = response.studentDetails;
        if (data) {
          detailsCacheByStudentId[student.id] = data;
          setDetails(data);
        }
      } catch (err) {
        console.error('Error fetching student details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStudentDetails();
  }, [student?.id]);

  useEffect(() => {
    if (!student?.id) return;
    let cancelled = false;
    setAttendanceSheetPeriod(undefined);
    setAttendanceSheetSessionId(null);
    setAttendanceSheetsLoading(true);

    const fetchAttendanceSheetData = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/attendance/sheet-data/${student.id}`, {
          headers: { ...getAuthHeaders() },
        });
        const json = await res.json();
        if (cancelled) return;
        const start = json?.data?.voucher?.training_period_start_date;
        const end = json?.data?.voucher?.training_period_end_date;
        const sessionId = json?.data?.student?.session || student.session || null;
        setAttendanceSheetSessionId(sessionId);
        if (start) {
          setAttendanceSheetPeriod({ start, end: end || null });
        } else {
          setAttendanceSheetPeriod(null);
        }
      } catch (_) {
        if (!cancelled) setAttendanceSheetPeriod(null);
      } finally {
        if (!cancelled) setAttendanceSheetsLoading(false);
      }
    };

    fetchAttendanceSheetData();
    return () => {
      cancelled = true;
    };
  }, [student?.id, student?.session]);

  const handleOpenAttendanceSheet = (start, end) => {
    const sessionId = attendanceSheetSessionId;
    const url = `/admin/students/${student.id}/attendance-sheet?start=${start}&end=${end}${sessionId ? `&sessionId=${sessionId}` : ''}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    let cancelled = false;
    fetchCareerCoaches({ activeOnly: true })
      .then((list) => {
        if (!cancelled) setCareerCoaches(list);
      })
      .catch(() => {
        if (!cancelled) setCareerCoaches([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const committedCareerCoachId = (() => {
    const coachId =
      details?.student?.careerCoachId ??
      details?.workforce?.careerCoachId ??
      student?.careerCoachId ??
      '';
    return coachId ? String(coachId) : '';
  })();

  useEffect(() => {
    setSelectedCareerCoachId(committedCareerCoachId);
    setCareerCoachEditMode(false);
  }, [committedCareerCoachId]);

  const careerCoachSelectionChanged =
    careerCoachEditMode && selectedCareerCoachId !== committedCareerCoachId;

  const handleCancelCareerCoachEdit = () => {
    setSelectedCareerCoachId(committedCareerCoachId);
    setCareerCoachEditMode(false);
  };

  const handleSaveCareerCoach = async () => {
    if (!student?.id) return;
    try {
      setSavingCareerCoach(true);
      const coachId = selectedCareerCoachId ? parseInt(selectedCareerCoachId, 10) : null;
      const result = await assignCareerCoachToStudent(student.id, coachId);
      const assigned = result.data || {};
      const nextDetails = details
        ? {
            ...details,
            student: {
              ...details.student,
              careerCoachId: assigned.careerCoachId,
              workforceCenter: assigned.workforceCenter || ''
            },
            workforce: {
              ...(details.workforce || {}),
              careerCoachId: assigned.careerCoachId,
              workforceCenter: assigned.workforceCenter || ''
            }
          }
        : details;
      if (nextDetails) {
        detailsCacheByStudentId[student.id] = nextDetails;
        setDetails(nextDetails);
      }
      if (onCareerCoachAssigned) {
        onCareerCoachAssigned(student.id, {
          careerCoachId: assigned.careerCoachId,
          workforceCenter: assigned.workforceCenter || ''
        });
      }
      showSuccess(coachId ? 'Career coach assigned' : 'Career coach removed');
      setCareerCoachEditMode(false);
    } catch (err) {
      showError(err.message || 'Failed to assign career coach');
    } finally {
      setSavingCareerCoach(false);
    }
  };

  const displayCareerCoachName = () => {
    const fromDetails =
      details?.student?.workforceCenter ||
      details?.workforce?.workforceCenter ||
      getStudentCareerCoach(student);
    if (fromDetails) return fromDetails;
    const match = careerCoaches.find((c) => String(c.id) === String(selectedCareerCoachId));
    return match?.name || 'N/A';
  };

  useEffect(() => {
    if (!student?.id) return;
    let cancelled = false;
    setTransferHistoryLoading(true);
    fetchStudentTransferHistory(student.id)
      .then((res) => {
        if (!cancelled) setTransferHistory(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setTransferHistory([]);
      })
      .finally(() => {
        if (!cancelled) setTransferHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [student?.id]);

  const formatTransferDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy hh:mm a');
    } catch {
      return dateString;
    }
  };

  const courseTransferLabel = (record, side) => {
    const code = side === 'old' ? record.old_course_code : record.new_course_code;
    const name = side === 'old' ? record.old_course_name : record.new_course_name;
    return (code && String(code).trim()) || name || (side === 'old' ? 'New Enrollment' : 'N/A');
  };

  // Initialize signature canvas
  const initSignatureCanvas = useCallback(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    canvas.style.width = '300px';
    canvas.style.height = '150px';
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width / ratio, canvas.height / ratio);
      ctx.strokeStyle = '#1a202c';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      setSignatureContext(ctx);
    }
  }, []);

  useEffect(() => {
    if (showSignatureModal) {
      // Reset signature state when modal opens
      setStudentSignature(details?.student?.signature || '');
      initSignatureCanvas();
      window.addEventListener('resize', initSignatureCanvas);
      return () => window.removeEventListener('resize', initSignatureCanvas);
    }
  }, [showSignatureModal, initSignatureCanvas, details?.student?.signature]);

  // Autofill signature pad when signature data is available
  useEffect(() => {
    if (!showSignatureModal || !studentSignature) return;
    
    // Use a small delay to ensure canvas is initialized
    const timeoutId = setTimeout(() => {
      const canvas = signatureCanvasRef.current;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      const img = new Image();
      img.onload = () => {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width / ratio, canvas.height / ratio);
        
        // Draw the signature image on the canvas
        const scale = Math.min(
          (canvas.width / ratio) / img.width,
          (canvas.height / ratio) / img.height
        );
        const x = ((canvas.width / ratio) - img.width * scale) / 2;
        const y = ((canvas.height / ratio) - img.height * scale) / 2;
        
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        
        // Update the context state to ensure drawing works after autofill
        setSignatureContext(ctx);
      };
      img.onerror = () => {
        console.error('Error loading signature image');
      };
      img.src = studentSignature;
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [studentSignature, showSignatureModal]);

  // Close "Add particular" dropdown when clicking outside
  useEffect(() => {
    if (!showAddParticularDropdown) return;
    const handleClickOutside = (e) => {
      if (addParticularDropdownRef.current && !addParticularDropdownRef.current.contains(e.target)) {
        setShowAddParticularDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddParticularDropdown]);

  const startSignatureDrawing = (e) => {
    if (!signatureContext) return;
    setIsDrawing(true);
    signatureContext.beginPath();
    signatureContext.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
  };

  const drawSignature = (e) => {
    if (!isDrawing || !signatureContext) return;
    signatureContext.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    signatureContext.stroke();
  };

  const stopSignatureDrawing = () => {
    if (!signatureContext) return;
    setIsDrawing(false);
    const signatureDataUrl = signatureCanvasRef.current.toDataURL();
    setStudentSignature(signatureDataUrl);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!signatureContext || !canvas) return;
    signatureContext.clearRect(0, 0, canvas.width, canvas.height);
    signatureContext.fillStyle = '#ffffff';
    signatureContext.fillRect(0, 0, canvas.width / (Math.max(window.devicePixelRatio || 1, 1)), canvas.height / (Math.max(window.devicePixelRatio || 1, 1)));
    setStudentSignature('');
  };

  const handleSaveSignature = async () => {
    if (!studentSignature) {
      showError('Please draw a signature');
      return;
    }

    setSavingSignature(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/students/${student.id}/signature`,
        { signature: studentSignature },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        showSuccess('Signature saved successfully');
        // Refresh student details
        const updatedResponse = await getStudentDetails(student.id);
        const next = updatedResponse.studentDetails;
        if (next && student?.id) detailsCacheByStudentId[student.id] = next;
        setDetails(next);
        setShowSignatureModal(false);
        setStudentSignature('');
      } else {
        showError(response.data.message || 'Failed to save signature');
      }
    } catch (error) {
      console.error('Error saving signature:', error);
      showError(error.response?.data?.message || 'Error saving signature');
    } finally {
      setSavingSignature(false);
    }
  };

  const handleParticularUpload = (particularName) => {
    setSelectedParticular(particularName);
    setSelectedParticularFiles([]);
    setShowParticularsModal(true);
  };

  const handleDocumentUpload = (documentType, documentName) => {
    setSelectedDocumentType(documentType);
    setShowDocumentModal(true);
  };

  const handleParticularUpdate = (particularName) => {
    setSelectedParticular(particularName);
    setSelectedParticularFiles([]);
    setShowUpdateModal(true);
  };

  const handleParticularFilesChange = (e) => {
    const list = e.target.files;
    setSelectedParticularFiles(list ? Array.from(list) : []);
  };

  const handleDocumentFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedDocumentFile(file);
  };

  const handleParticularSubmit = async (e) => {
    e.preventDefault();
    if (!selectedParticularFiles.length) {
      showError('Please select at least one file');
      return;
    }

    setUploadingParticular(true);
    try {
      await uploadParticular(student.id, selectedParticular, selectedParticularFiles);
      showSuccess(
        selectedParticularFiles.length > 1
          ? `${selectedParticular}: ${selectedParticularFiles.length} files uploaded`
          : `${selectedParticular} uploaded successfully`
      );

      const response = await getStudentDetails(student.id);
      const next = response.studentDetails;
      if (next && student?.id) detailsCacheByStudentId[student.id] = next;
      setDetails(next);

      setShowParticularsModal(false);
      setSelectedParticular(null);
      setSelectedParticularFiles([]);
    } catch (error) {
      showError(error.message || 'Error uploading particular');
    } finally {
      setUploadingParticular(false);
    }
  };

  const handleParticularUpdateSubmit = async (e) => {
    e.preventDefault();
    if (!selectedParticularFiles.length) {
      showError('Please select at least one file');
      return;
    }

    setUpdatingParticular(true);
    try {
      await updateParticular(student.id, selectedParticular, selectedParticularFiles);
      showSuccess(
        selectedParticularFiles.length > 1
          ? `${selectedParticular}: ${selectedParticularFiles.length} files added`
          : `${selectedParticular} updated successfully`
      );

      const response = await getStudentDetails(student.id);
      const next = response.studentDetails;
      if (next && student?.id) detailsCacheByStudentId[student.id] = next;
      setDetails(next);

      setShowUpdateModal(false);
      setSelectedParticular(null);
      setSelectedParticularFiles([]);
    } catch (error) {
      showError(error.message || 'Error updating particular');
    } finally {
      setUpdatingParticular(false);
    }
  };

  const handleRemoveOneParticularFile = async (filePath) => {
    if (!filePath || !selectedParticular) return;
    setRemovingParticularFilePath(filePath);
    try {
      await deleteParticularFile(student.id, selectedParticular, filePath);
      showSuccess('File removed');
      const response = await getStudentDetails(student.id);
      const next = response.studentDetails;
      if (next && student?.id) detailsCacheByStudentId[student.id] = next;
      setDetails(next);
    } catch (error) {
      showError(error.message || 'Error removing file');
    } finally {
      setRemovingParticularFilePath(null);
    }
  };

  const handleParticularDelete = (particularName) => {
    setSelectedParticular(particularName);
    setShowDeleteParticularModal(true);
  };

  const handleParticularDeleteConfirm = async () => {
    setDeletingParticular(true);
    try {
      await deleteParticular(student.id, selectedParticular);
      showSuccess(`${selectedParticular} deleted successfully`);

      // Refresh student details to show the updated particulars
      const response = await getStudentDetails(student.id);
      const next = response.studentDetails;
      if (next && student?.id) detailsCacheByStudentId[student.id] = next;
      setDetails(next);

      // Close modal and reset state
      setShowDeleteParticularModal(false);
      setSelectedParticular(null);
    } catch (error) {
      showError(error.message || 'Error deleting particular');
    } finally {
      setDeletingParticular(false);
    }
  };

  const closeDeleteParticularModal = () => {
    setShowDeleteParticularModal(false);
    setSelectedParticular(null);
  };

  const closeParticularsModal = () => {
    setShowParticularsModal(false);
    setSelectedParticular(null);
    setSelectedParticularFiles([]);
  };

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    setSelectedParticular(null);
    setSelectedParticularFiles([]);
  };

  const handleDocumentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDocumentFile) {
      showError('Please select a file');
      return;
    }

    // Check if a file already exists for this document type
    let existingFile = false;
    let documentName = '';

    if (selectedDocumentType === 'social_security_upload' && details.student.ssnUpload && details.student.ssnUpload !== 'N/A' && details.student.ssnUpload !== 'null') {
      existingFile = true;
      documentName = 'SSN Document';
    } else if (selectedDocumentType === 'dl_upload' && details.student.dlUpload && details.student.dlUpload !== 'N/A' && details.student.dlUpload !== 'null') {
      existingFile = true;
      documentName = 'DL or State ID Document';
    } else if (selectedDocumentType === 'medical_evaluation_form' && details.student.medicalEvaluationFormPath && details.student.medicalEvaluationFormPath !== 'N/A' && details.student.medicalEvaluationFormPath !== 'null') {
      existingFile = true;
      documentName = 'Medical Evaluation Form';
    }

    if (existingFile) {
      // Show confirmation dialog for updating existing file
      setPendingDocumentUpdate({
        documentType: selectedDocumentType,
        documentName: documentName,
        file: selectedDocumentFile
      });
      setShowUpdateConfirm(true);
      return;
    }

    // No existing file, proceed with upload
    await performDocumentUpload();
  };

  const performDocumentUpload = async () => {
    setUploadingDocument(true);
    try {
      // Use the new document upload API function
      const result = await uploadStudentDocument(student.id, selectedDocumentType, selectedDocumentFile);

      if (result.success) {
        let successMessage = '';
        if (selectedDocumentType === 'social_security_upload') {
          successMessage = 'SSN Document uploaded successfully';
        } else if (selectedDocumentType === 'dl_upload') {
          successMessage = 'DL or State ID Document uploaded successfully';
        } else if (selectedDocumentType === 'medical_evaluation_form') {
          successMessage = 'Medical Evaluation Form uploaded successfully';
        } else {
          successMessage = 'Document uploaded successfully';
        }

        showSuccess(successMessage);

        // Refresh student details to show the new document
        const updatedResponse = await getStudentDetails(student.id);
        const next = updatedResponse.studentDetails;
        if (next && student?.id) detailsCacheByStudentId[student.id] = next;
        setDetails(next);

        // Close modal and reset state
        setShowDocumentModal(false);
        setSelectedDocumentType(null);
        setSelectedDocumentFile(null);
      } else {
        showError(result.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Document upload error:', error);
      showError(error.message || 'Error uploading document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const closeDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedDocumentType(null);
    setSelectedDocumentFile(null);
  };

  const handleUpdateConfirm = async () => {
    if (pendingDocumentUpdate) {
      // Set the file and document type for upload
      setSelectedDocumentFile(pendingDocumentUpdate.file);
      setSelectedDocumentType(pendingDocumentUpdate.documentType);

      // Close confirmation modal
      setShowUpdateConfirm(false);
      setPendingDocumentUpdate(null);

      // Proceed with the upload
      await performDocumentUpload();
    }
  };

  const handleUpdateCancel = () => {
    setShowUpdateConfirm(false);
    setPendingDocumentUpdate(null);
    // Also close the document modal if it's open
    setShowDocumentModal(false);
    setSelectedDocumentType(null);
    setSelectedDocumentFile(null);
  };

  if (!student) return null;

  const displayDetails = details || (student ? {
    student: {
      id: student.id,
      registrationNumber: '—',
      lastName: student.lastName || '—',
      firstName: student.firstName || '—',
      dateOfBirth: null,
      gender: student.gender || '—',
      email: student.email || '—',
      phone: student.phone || '—',
      address: '—',
      emergencyContact: '—',
      otherEmergencyContact: '—',
      emergencyContactPhone: '—',
      course: '—',
      studentNotes: '—',
      session: '—',
      nationality: '—',
      dateOfJoining: null,
      socialSecurityNumber: '—',
      ssnUpload: null,
      dlUpload: null,
      medicalEvaluationFormPath: null
    },
    attendance: [],
    documents: [],
    workforce: {},
    vouchers: []
  } : null);
  const isPartial = !details;

  if (error) {
    return (
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Error</ModalTitle>
            <ModalClose onClick={onClose}>&times;</ModalClose>
          </ModalHeader>
          <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
            Error loading student details: {error}
          </div>
        </ModalContent>
      </ModalOverlay>
    );
  }

  if (!displayDetails) {
    return (
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>No Data</ModalTitle>
            <ModalClose onClick={onClose}>&times;</ModalClose>
          </ModalHeader>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            No student details found.
          </div>
        </ModalContent>
      </ModalOverlay>
    );
  }
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Overview</ModalTitle>
          <ModalClose onClick={onClose}>&times;</ModalClose>
        </ModalHeader>
        <div style={{ padding: '0 40px 0px 15px' }}>
          <DetailsTable style={{ display: 'inline-table' }}>
            <tbody style={{ textAlign: 'left' }}>
              <tr><th>Registration Number</th><td>{displayDetails.student.registrationNumber || 'N/A'}</td></tr>
              <tr><th>Last Name</th><td>{displayDetails.student.lastName || 'N/A'}</td></tr>
              <tr><th>First Name</th><td>{displayDetails.student.firstName || 'N/A'}</td></tr>
              <tr><th>Date of Birth</th><td>{displayDetails.student.dateOfBirth ? new Date(displayDetails.student.dateOfBirth).toLocaleDateString() : 'N/A'}</td></tr>
              <tr><th>Gender</th><td>{displayDetails.student.gender || 'N/A'}</td></tr>
              <tr><th>Email Address</th><td>{displayDetails.student.email || 'N/A'}</td></tr>
              <tr><th>Contact Number</th><td>{displayDetails.student.phone || 'N/A'}</td></tr>
              <tr><th>Address</th><td>{displayDetails.student.address || 'N/A'}</td></tr>
              <tr><th>Emergency Contact Info</th><td>{displayDetails.student.emergencyContact || 'N/A'}</td></tr>
              <tr><th>Other Emergency Contact</th><td>{displayDetails.student.otherEmergencyContact || 'N/A'}</td></tr>
              <tr><th>Emergency Contact Phone</th><td>{displayDetails.student.emergencyContactPhone || 'N/A'}</td></tr>
              <tr><th>Course</th><td>{displayDetails.student.course || 'N/A'}</td></tr>
              <tr><th>Student Notes</th><td>{displayDetails.student.studentNotes || 'N/A'}</td></tr>
              <tr><th>Session</th><td>{displayDetails.student.session || 'N/A'}</td></tr>
              <tr>
                <th>Career Coach</th>
                <td>
                  {canUpdateStudent && !isPartial ? (
                    <CareerCoachFieldRow>
                      {!careerCoachEditMode ? (
                        <>
                          <CareerCoachValueText>
                            {displayCareerCoachName()}
                          </CareerCoachValueText>
                          <UploadButton
                            type="button"
                            onClick={() => setCareerCoachEditMode(true)}
                            style={{
                              background: 'rgb(51, 66, 147)',
                              color: 'white',
                              fontSize: '0.8rem',
                              padding: '6px 12px',
                              flexShrink: 0
                            }}
                          >
                            Update
                          </UploadButton>
                        </>
                      ) : (
                        <>
                          <CareerCoachSelect
                            value={selectedCareerCoachId}
                            onChange={(e) => setSelectedCareerCoachId(e.target.value)}
                          >
                            <option value="">N/A</option>
                            {careerCoaches.map((coach) => (
                              <option key={coach.id} value={String(coach.id)}>
                                {coach.name}
                              </option>
                            ))}
                          </CareerCoachSelect>
                          {careerCoachSelectionChanged ? (
                            <UploadButton
                              type="button"
                              onClick={handleSaveCareerCoach}
                              disabled={savingCareerCoach}
                              style={{
                                background: 'rgb(51, 66, 147)',
                                color: 'white',
                                fontSize: '0.8rem',
                                padding: '6px 12px',
                                flexShrink: 0
                              }}
                            >
                              {savingCareerCoach ? 'Updating...' : 'Update'}
                            </UploadButton>
                          ) : (
                            <CareerCoachCancelButton
                              type="button"
                              onClick={handleCancelCareerCoachEdit}
                              title="Cancel"
                              aria-label="Cancel career coach update"
                            >
                              <FaTimes />
                            </CareerCoachCancelButton>
                          )}
                        </>
                      )}
                    </CareerCoachFieldRow>
                  ) : (
                    displayCareerCoachName()
                  )}
                </td>
              </tr>
              <tr><th>Nationality</th><td>{displayDetails.student.nationality || 'N/A'}</td></tr>
              <tr><th>Date of Joining</th><td>{displayDetails.student.dateOfJoining ? new Date(displayDetails.student.dateOfJoining).toLocaleDateString() : 'N/A'}</td></tr>
              <tr><th>Social Security Number</th><td>{displayDetails.student.socialSecurityNumber || 'N/A'}</td></tr>
              <tr><th>SSN Upload</th><td>
                {isPartial ? '—' : (() => {
                  const ssnUpload = details.student.ssnUpload;

                  if (ssnUpload && ssnUpload !== 'N/A' && ssnUpload !== 'null') {
                    const secureUrl = buildFileUrl(ssnUpload);
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <FileLink 
                          onClick={() => handleViewFile(ssnUpload, 'document', 'SSN')}
                          style={{ cursor: 'pointer' }}
                        >
                          View SSN
                        </FileLink>
                        <UploadButton
                          onClick={() => handleDocumentUpload('social_security_upload', 'SSN Document')}
                          style={{ background: 'rgb(51, 66, 147)', color: 'white', fontSize: '0.8rem', padding: '6px 12px', marginLeft: '20px' }}
                        >
                          <FaUpload style={{ marginRight: '4px' }} />
                          Update
                        </UploadButton>
                      </div>
                    );
                  } else {
                    return (
                      <UploadButton onClick={() => handleDocumentUpload('social_security_upload', 'SSN Document')}>
                        <FaUpload style={{ marginRight: '8px' }} />
                        Upload SSN Document
                      </UploadButton>
                    );
                  }
                })()}
              </td></tr>
              <tr><th>DL or State ID Upload</th><td>
                {isPartial ? '—' : (() => {
                  const dlUpload = details.student.dlUpload;

                  if (dlUpload && dlUpload !== 'N/A' && dlUpload !== 'null') {
                    const secureUrl = buildFileUrl(dlUpload);
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <FileLink 
                          onClick={() => handleViewFile(dlUpload, 'document', 'DL-or-State-ID')}
                          style={{ cursor: 'pointer' }}
                        >
                          View Driving License
                        </FileLink>
                        <UploadButton
                          onClick={() => handleDocumentUpload('dl_upload', 'DL or State ID Document')}
                          style={{ background: 'rgb(51, 66, 147)', color: 'white', fontSize: '0.8rem', padding: '6px 12px', marginLeft: '20px' }}
                        >
                          <FaUpload style={{ marginRight: '4px' }} />
                          Update
                        </UploadButton>
                      </div>
                    );
                  } else {
                    return (
                      <UploadButton onClick={() => handleDocumentUpload('dl_upload', 'DL or State ID Document')}>
                        <FaUpload style={{ marginRight: '8px' }} />
                        Upload DL or State ID Document
                      </UploadButton>
                    );
                  }
                })()}
              </td></tr>
              <tr><th>Medical Evaluation Form</th><td>
                {isPartial ? '—' : (() => {
                  const medicalEvalPath = details.student.medicalEvaluationFormPath;

                  if (medicalEvalPath && medicalEvalPath !== 'N/A' && medicalEvalPath !== 'null') {
                    const secureUrl = buildFileUrl(medicalEvalPath);
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <FileLink 
                          onClick={() => handleViewFile(medicalEvalPath, 'document', 'Medical-Evaluation-Form')}
                          style={{ cursor: 'pointer' }}
                        >
                          View Medical Evaluation Form
                        </FileLink>
                        <UploadButton
                          onClick={() => handleDocumentUpload('medical_evaluation_form', 'Medical Evaluation Form')}
                          style={{ background: 'rgb(51, 66, 147)', color: 'white', fontSize: '0.8rem', padding: '6px 12px', marginLeft: '20px' }}
                        >
                          <FaUpload style={{ marginRight: '4px' }} />
                          Update
                        </UploadButton>
                      </div>
                    );
                  } else {
                    return (
                      <UploadButton onClick={() => handleDocumentUpload('medical_evaluation_form', 'Medical Evaluation Form')}>
                        <FaUpload style={{ marginRight: '8px' }} />
                        Upload Medical Evaluation Form
                      </UploadButton>
                    );
                  }
                })()}
              </td></tr>
              <tr><th>Status</th><td>{displayDetails.student.status || 'N/A'}</td></tr>
              <tr><th>Added Time</th><td>{displayDetails.student.addedTime ? new Date(displayDetails.student.addedTime).toLocaleString() : 'N/A'}</td></tr>

            </tbody>
          </DetailsTable>

          {isPartial && (
            <div style={{ padding: '16px', textAlign: 'center', color: '#6c757d', fontSize: '0.9rem' }}>
              Loading remaining details...
            </div>
          )}

          {!isPartial && (
          <>
          {/* Vouchers Section */}
          {details.vouchers && details.vouchers.length > 0 && (
            <>
              <SectionTitle>Vouchers</SectionTitle>
              {details.vouchers.map((voucher, idx) => (
                <DetailsTable key={voucher.id || idx} style={{ marginBottom: 24, display: 'inline-table' }}>
                  <tbody>
                    <tr><th>Program Name</th><td>{voucher.program_name || 'N/A'}</td></tr>
                    <tr><th>Referring WIA Provider</th><td>{voucher.referring_wia_provider || 'N/A'}</td></tr>
                    <tr><th>Training Provider</th><td>{voucher.training_provider || 'N/A'}</td></tr>
                    <tr><th>Case Manager Name</th><td>{voucher.case_manager_name || 'N/A'}</td></tr>
                    <tr><th>Case Manager Telephone</th><td>{voucher.case_manager_telephone || 'N/A'}</td></tr>
                    <tr><th>Instructor Name</th><td>{voucher.instructor_name || 'N/A'}</td></tr>
                    <tr><th>Training Period Start</th><td>{voucher.training_period_start_date ? new Date(voucher.training_period_start_date).toLocaleDateString() : 'N/A'}</td></tr>
                    <tr><th>Training Period End</th><td>{voucher.training_period_end_date ? new Date(voucher.training_period_end_date).toLocaleDateString() : 'N/A'}</td></tr>
                    <tr><th>Voucher File</th><td>{voucher.voucher_file_path ? (
                      <FileLink 
                        onClick={() => handleViewFile(voucher.voucher_file_path, 'document', 'Voucher')}
                        style={{ cursor: 'pointer' }}
                      >
                        View Voucher
                      </FileLink>
                    ) : 'N/A'}</td></tr>
                  </tbody>
                </DetailsTable>
              ))}
            </>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <SectionTitle style={{ paddingTop: '32px', marginBottom: '12px' }}>Signed Documents</SectionTitle>
            <SignatureButton onClick={() => {
              setShowSignatureModal(true);
            }}>
              {details?.student?.signature && details.student.signature.trim() !== '' ? 'Update Signature' : 'Add Signature'}
            </SignatureButton>
          </div>
          <DetailsTable style={{ textAlign: 'left' }}>
            <tbody>
              {!details.student.enrollment ? (
                <tr>
                  <td colSpan={4} style={{ fontStyle: 'italic', color: '#6c757d', padding: '20px' }}>
                    The student has not signed the enrollment forms yet
                  </td>
                </tr>
              ) : (
                <>
                  <tr>
                    <th>Enrollment Agreement Form</th>
                    <td colSpan={3}>
                      <FileLink
                        as="button"
                        style={{ background: 'none', border: 'none', color: '#3366cc', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.97rem' }}
                        onClick={() => openSignedDocument(`/admin/enrollment-agreement-form/${details.student.id}`, 'Enrollment-Agreement-Form')}
                      >
                        View Enrollment Agreement Form
                      </FileLink>
                    </td>
                  </tr>

                  <tr>
                    <th>Medical Evaluation Form</th>
                    <td colSpan={3}>
                      <FileLink
                        as="button"
                        style={{ background: 'none', border: 'none', color: '#3366cc', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.97rem' }}
                        onClick={() => openSignedDocument(`/admin/medical-evaluation-form/${details.student.id}`, 'Medical-Evaluation-Form')}
                      >
                        View Medical Evaluation Form
                      </FileLink>
                    </td>
                  </tr>
                  <tr>
                    <th>Student ID Form</th>
                    <td colSpan={3}>
                      <FileLink
                        as="button"
                        style={{ background: 'none', border: 'none', color: '#3366cc', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.97rem' }}
                        onClick={() => openSignedDocument(`/admin/student-id-form/${details.student.id}`, 'Student-ID-Form')}
                      >
                        View Student ID Form
                      </FileLink>
                    </td>
                  </tr>
                  <tr>
                    <th>Uniform Size Form</th>
                    <td colSpan={3}>
                      <FileLink
                        as="button"
                        style={{ background: 'none', border: 'none', color: '#3366cc', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.97rem' }}
                        onClick={() => openSignedDocument(`/admin/uniform-size-form/${details.student.id}`, 'Uniform-Size-Form')}
                      >
                        View Uniform Size Form
                      </FileLink>
                    </td>
                  </tr>
                  <tr>
                    <th>Consent to Test and Release Information</th>
                    <td colSpan={3}>
                      <FileLink
                        as="button"
                        style={{ background: 'none', border: 'none', color: '#3366cc', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.97rem' }}
                        onClick={() => openSignedDocument(`/admin/consent-release-info-form/${details.student.id}`, 'Consent-to-Test-and-Release-Information')}
                      >
                        View Consent to Test and Release Information
                      </FileLink>
                    </td>
                  </tr>

                  <tr>
                    <th>Background Check Form</th>
                    <td colSpan={3}>
                      <FileLink
                        as="button"
                        style={{ background: 'none', border: 'none', color: '#3366cc', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.97rem' }}
                        onClick={() => openSignedDocument(`/admin/background-check-form/${details.student.id}`, 'Background-Check-Form')}
                      >
                        View Background Check Form
                      </FileLink>
                    </td>
                  </tr>
                  <tr>
                    <th>Student Acknowledgment Form</th>
                    <td colSpan={3}>
                      <FileLink
                        as="button"
                        style={{ background: 'none', border: 'none', color: '#3366cc', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.97rem' }}
                        onClick={() => openSignedDocument(`/admin/student-acknowledgment/${details.student.id}`, 'Student-Acknowledgment-Form')}
                      >
                        View Student Acknowledgment Form
                      </FileLink>
                    </td>
                  </tr>
                  <tr>
                    <th>Media Release Form</th>
                    <td colSpan={3}>
                      <FileLink
                        as="button"
                        style={{ background: 'none', border: 'none', color: '#3366cc', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.97rem' }}
                        onClick={() => openSignedDocument(`/admin/media-release-form/${details.student.id}`, 'Media-Release-Form')}
                      >
                        View Media Release Form
                      </FileLink>
                    </td>
                  </tr>
                  <tr>
                    <th>Medical Supplies Acknowledgment Form</th>
                    <td colSpan={3}>
                      <FileLink
                        as="button"
                        style={{ background: 'none', border: 'none', color: '#3366cc', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.97rem' }}
                        onClick={() => openSignedDocument(`/admin/medical-supplies-acknowledgment-form/${details.student.id}`, 'Medical-Supplies-Acknowledgment-Form')}
                      >
                        View Medical Supplies Acknowledgment Form
                      </FileLink>
                    </td>
                  </tr>
                  <tr>
                    <th>COVID-19 Vaccination Attestation Form</th>
                    <td colSpan={3}>
                      <FileLink
                        as="button"
                        style={{ background: 'none', border: 'none', color: '#3366cc', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.97rem' }}
                        onClick={() => openSignedDocument(`/admin/covid-vaccination-attestation-form/${details.student.id}`, 'COVID-19-Vaccination-Attestation-Form')}
                      >
                        View COVID-19 Vaccination Attestation Form
                      </FileLink>
                    </td>
                  </tr>
                  <tr>
                    <th>Cell Phone Policy Form</th>
                    <td colSpan={3}>
                      <FileLink
                        as="button"
                        style={{ background: 'none', border: 'none', color: '#3366cc', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.97rem' }}
                        onClick={() => openSignedDocument(`/admin/cell-phone-policy-form/${details.student.id}`, 'Cell-Phone-Policy-Form')}
                      >
                        View Cell Phone Policy Form
                      </FileLink>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </DetailsTable>

          {/* Particulars Section */}
          <ParticularsSection>
            <ParticularsSectionHeader>
              <SectionTitle>Particulars</SectionTitle>
              {particulars && particulars.length > 0 && particulars.some((p) => getStudentParticularPaths(details.student.particulars && details.student.particulars[p.particular_name]).length === 0) && (
                <AddParticularButtonWrap ref={addParticularDropdownRef}>
                  <AddParticularButton
                    type="button"
                    onClick={() => setShowAddParticularDropdown((v) => !v)}
                  >
                    <FaPlus /> Add record
                  </AddParticularButton>
                  {showAddParticularDropdown && (
                    <AddParticularDropdown>
                      {particulars
                        .filter((p) => getStudentParticularPaths(details.student.particulars && details.student.particulars[p.particular_name]).length === 0)
                        .map((particular) => (
                          <AddParticularDropdownItem
                            key={particular.id}
                            type="button"
                            onClick={() => {
                              handleParticularUpload(particular.particular_name);
                              setShowAddParticularDropdown(false);
                            }}
                          >
                            {particular.particular_name}
                          </AddParticularDropdownItem>
                        ))}
                    </AddParticularDropdown>
                  )}
                </AddParticularButtonWrap>
              )}
            </ParticularsSectionHeader>
            <ParticularsGrid>
              {particulars && particulars.length > 0 ? (
                (() => {
                  const usedParticulars = particulars.filter(
                    (p) => getStudentParticularPaths(details.student.particulars && details.student.particulars[p.particular_name]).length > 0
                  );
                  return usedParticulars.length > 0 ? (
                    usedParticulars.map((particular) => {
                      const paths = getStudentParticularPaths(details.student.particulars[particular.particular_name]);
                      return (
                      <ParticularsItem key={particular.id}>
                        <ParticularsItemTitle>{particular.particular_name}</ParticularsItemTitle>
                        <ParticularsFileLinksWrap>
                          {paths.map((filePath, idx) => (
                            <ParticularsFileLink
                              key={filePath}
                              onClick={() => handleViewFile(filePath, 'document', particular.particular_name)}
                              style={{ cursor: 'pointer' }}
                            >
                              View {paths.length > 1 ? `${particular.particular_name} (${idx + 1})` : particular.particular_name}
                              {paths.length > 1 && (
                                <span style={{ color: '#6c757d', fontWeight: 400 }}> — {filePath.split('/').pop()}</span>
                              )}
                            </ParticularsFileLink>
                          ))}
                        </ParticularsFileLinksWrap>
                        <ParticularsUpdateButton onClick={() => handleParticularUpdate(particular.particular_name)}>
                          <FaPencilAlt /> Update
                        </ParticularsUpdateButton>
                      </ParticularsItem>
                    );})
                  ) : (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#6c757d', fontStyle: 'italic' }}>
                      No particulars added yet. Use &quot;Add particular&quot; to add one.
                    </div>
                  );
                })()
              ) : (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#6c757d', fontStyle: 'italic' }}>
                  No particulars available
                </div>
              )}
            </ParticularsGrid>
          </ParticularsSection>

          {/* <SectionTitle>Signed ITA Attendance Details</SectionTitle>
          <DetailsTable>
            <tbody style={{textAlign: 'left'}}>
              <tr><th>Date</th><th>Status</th><th>Hours Completed</th><th>Instructor</th><th>Course</th></tr>
              {details.itaAttendance && details.itaAttendance.length > 0 ? (
                details.itaAttendance.map((att, idx) => (
                  <tr key={idx}>
                    <td>{att.date ? new Date(att.date).toLocaleDateString() : 'N/A'}</td>
                    <td>{att.status || 'N/A'}</td>
                    <td>{att.hoursCompleted || 'N/A'}</td>
                    <td>{att.instructor || 'N/A'}</td>
                    <td>{att.course || 'N/A'}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={5}>No ITA attendance records found</td></tr>
              )}
            </tbody>
          </DetailsTable> */}

          {/* <SectionTitle>Previous Course Details</SectionTitle>
          <DetailsTable>
            <tbody style={{textAlign: 'left'}}>
              <tr><th>Course</th><th>Session</th><th>Moved Date</th><th>Student Course Status</th></tr>
              {details.previousCourses && details.previousCourses.length > 0 ? (
                details.previousCourses.map((c, idx) => (
                  <tr key={idx}>
                    <td>{c.course || 'N/A'}</td>
                    <td>{c.session || 'N/A'}</td>
                    <td>{c.movedDate ? new Date(c.movedDate).toLocaleDateString() : 'N/A'}</td>
                    <td>{c.status || 'N/A'}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={4}>No previous course records found</td></tr>
              )}
            </tbody>
          </DetailsTable>

          <SectionTitle>Grade</SectionTitle>
          <DetailsTable>
            <tbody style={{textAlign: 'left'}}>
              <tr><th>Session</th><th>Category</th><th>Name</th><th>Max Points</th><th>Points Scored</th></tr>
              {details.grades && details.grades.length > 0 ? (
                details.grades.map((g, idx) => (
                  <tr key={idx}>
                    <td>{g.session || 'N/A'}</td>
                    <td>{g.category || 'N/A'}</td>
                    <td>{g.name || 'N/A'}</td>
                    <td>{g.maxPoints || 'N/A'}</td>
                    <td>{g.pointsScored || 'N/A'}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={5}>No grade records found</td></tr>
              )}
            </tbody>
          </DetailsTable> */}

          <SectionTitle>Attendance Sheets</SectionTitle>
          <div style={{ marginBottom: 30 }}>
            {attendanceSheetsLoading || attendanceSheetPeriod === undefined ? (
              <div style={{ color: '#6c757d', fontStyle: 'italic' }}>Loading attendance sheets...</div>
            ) : attendanceSheetPeriod === null || !attendanceSheetPeriod.start ? (
              <div style={{ color: '#6c757d' }}>
                Please upload voucher for the student and their attendance record to see their attendance sheets.
              </div>
            ) : (() => {
              const ranges = buildFourWeekAttendanceRanges(
                new Date(attendanceSheetPeriod.start),
                attendanceSheetPeriod.end || null
              );
              if (ranges.length === 0) {
                return <div style={{ color: '#6c757d' }}>No attendance sheets available yet.</div>;
              }
              return (
                <div>
                  {[...ranges].reverse().map((r) => (
                    <FileLink
                      key={r.urlStart}
                      as="button"
                      type="button"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#0d6efd',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        display: 'block',
                        marginBottom: 8,
                        fontSize: '0.97rem',
                        textAlign: 'left',
                        padding: 0,
                      }}
                      onClick={() => handleOpenAttendanceSheet(r.urlStart, r.urlEnd)}
                    >
                      {`Attendance sheet for ${r.labelStart} to ${r.labelEnd}`}
                    </FileLink>
                  ))}
                </div>
              );
            })()}
          </div>

          <SectionTitle>Course Transfer History</SectionTitle>
          <DetailsTable style={{ marginBottom: 30, display: 'inline-table' }}>
            <tbody style={{ textAlign: 'left' }}>
              <tr>
                <th>Date &amp; Time</th>
                <th>Transfer Details</th>
                <th>Reason</th>
                <th>Notes</th>
              </tr>
              {transferHistoryLoading ? (
                <tr>
                  <td colSpan={4} style={{ color: '#6c757d', fontStyle: 'italic' }}>
                    Loading transfer history…
                  </td>
                </tr>
              ) : transferHistory.length > 0 ? (
                transferHistory.map((record) => (
                  <tr key={record.id}>
                    <td>{formatTransferDate(record.transfer_date)}</td>
                    <td>
                      <OverviewTransferFlow>
                        <div>
                          <OverviewTransferCourseTitle>{courseTransferLabel(record, 'old')}</OverviewTransferCourseTitle>
                          <OverviewTransferSessionLine>
                            {record.old_session_name || '-'}
                          </OverviewTransferSessionLine>
                        </div>
                        <OverviewTransferArrow aria-hidden />
                        <div>
                          <OverviewTransferCourseTitle>{courseTransferLabel(record, 'new')}</OverviewTransferCourseTitle>
                          <OverviewTransferSessionLine>
                            {record.new_session_name || '-'}
                          </OverviewTransferSessionLine>
                        </div>
                      </OverviewTransferFlow>
                    </td>
                    <td>
                      {record.transfer_reason ? (
                        <TransferReasonBadge $type={record.transfer_reason}>{record.transfer_reason}</TransferReasonBadge>
                      ) : (
                        <span style={{ color: '#6c757d', fontStyle: 'italic' }}>Not specified</span>
                      )}
                    </td>
                    <td>
                      {record.transfer_notes ? (
                        <div style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {record.transfer_notes}
                        </div>
                      ) : (
                        <span style={{ color: '#6c757d', fontStyle: 'italic' }}>-</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>No course transfer history found.</td>
                </tr>
              )}
            </tbody>
          </DetailsTable>

          <SectionTitle>Credentials</SectionTitle>
          <DetailsTable style={{ marginBottom: 30 }}>
            <tbody style={{ textAlign: 'left' }}>
              <tr><th>Login ID</th><td>{details.student.loginId || 'N/A'}</td></tr>
              <tr><th>Password</th><td>{details.student.password || 'N/A'}</td></tr>
            </tbody>
          </DetailsTable>

          <hr style={{ marginBottom: 30 }} />
          </>
          )}
        </div>
      </ModalContent>

      {/* Particulars Upload Modal */}
      {showParticularsModal && (
        <ParticularsModalOverlay onClick={closeParticularsModal}>
          <ParticularsModalContent onClick={(e) => e.stopPropagation()}>
            <ParticularsModalHeader>
              <ParticularsModalTitle>Upload {selectedParticular}</ParticularsModalTitle>
              <CloseButton onClick={closeParticularsModal}>
                <FaTimes />
              </CloseButton>
            </ParticularsModalHeader>

            <ParticularsModalBody>
              <form onSubmit={handleParticularSubmit}>
                <ParticularsFormGroup>
                  <ParticularsLabel>Select one or more files</ParticularsLabel>
                  <ParticularsInput
                    type="file"
                    onChange={handleParticularFilesChange}
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                    multiple
                    required
                  />
                </ParticularsFormGroup>

                <ParticularsButtonGroup>
                  <ParticularsCancelButton type="button" onClick={closeParticularsModal}>
                    Cancel
                  </ParticularsCancelButton>
                  <ParticularsSubmitButton type="submit" disabled={uploadingParticular || selectedParticularFiles.length === 0}>
                    {uploadingParticular ? 'Uploading...' : 'Upload'}
                  </ParticularsSubmitButton>
                </ParticularsButtonGroup>
              </form>
            </ParticularsModalBody>
          </ParticularsModalContent>
        </ParticularsModalOverlay>
      )}

      {/* Particulars Update Modal */}
      {showUpdateModal && (
        <ParticularsModalOverlay onClick={closeUpdateModal}>
          <ParticularsModalContent onClick={(e) => e.stopPropagation()}>
            <ParticularsModalHeader>
              <ParticularsModalTitle>Manage {selectedParticular}</ParticularsModalTitle>
              <CloseButton onClick={closeUpdateModal}>
                <FaTimes />
              </CloseButton>
            </ParticularsModalHeader>

            <ParticularsModalBody>
              <form onSubmit={handleParticularUpdateSubmit}>
                {selectedParticular && details?.student?.particulars && getStudentParticularPaths(details.student.particulars[selectedParticular]).length > 0 && (
                  <ParticularsExistingFilesBox>
                    <ParticularsLabel style={{ marginBottom: 4 }}>Current files</ParticularsLabel>
                    {getStudentParticularPaths(details.student.particulars[selectedParticular]).map((filePath) => (
                      <ParticularsExistingFileRow key={filePath}>
                        <span style={{ wordBreak: 'break-all', color: '#2c3e50' }}>{filePath.split('/').pop()}</span>
                        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <ParticularsFileLink
                            as="button"
                            type="button"
                            onClick={() => handleViewFile(filePath, 'document', selectedParticular)}
                            style={{ cursor: 'pointer', marginTop: 0, border: 'none', background: 'none', padding: 0 }}
                          >
                            View
                          </ParticularsFileLink>
                          <ParticularsSmallRemoveBtn
                            type="button"
                            disabled={removingParticularFilePath === filePath}
                            onClick={() => handleRemoveOneParticularFile(filePath)}
                          >
                            {removingParticularFilePath === filePath ? '…' : 'Remove'}
                          </ParticularsSmallRemoveBtn>
                        </span>
                      </ParticularsExistingFileRow>
                    ))}
                  </ParticularsExistingFilesBox>
                )}

                <ParticularsFormGroup>
                  <ParticularsLabel>Add more files</ParticularsLabel>
                  <ParticularsInput
                    type="file"
                    onChange={handleParticularFilesChange}
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                    multiple
                    required
                  />
                </ParticularsFormGroup>

                <ParticularsButtonGroup style={{ justifyContent: 'space-between' }}>
                  <ParticularsDeleteButton 
                    type="button" 
                    onClick={() => {
                      closeUpdateModal();
                      handleParticularDelete(selectedParticular);
                    }}
                    style={{ position: 'static', marginTop: 0 }}
                  >
                    <FaTrash /> Delete all
                  </ParticularsDeleteButton>
                  <div style={{ display: 'flex', gap: '15px' }}>
                    <ParticularsCancelButton type="button" onClick={closeUpdateModal}>
                      Cancel
                    </ParticularsCancelButton>
                    <ParticularsSubmitButton type="submit" disabled={updatingParticular || selectedParticularFiles.length === 0}>
                      {updatingParticular ? 'Adding...' : 'Add files'}
                    </ParticularsSubmitButton>
                  </div>
                </ParticularsButtonGroup>
              </form>
            </ParticularsModalBody>
          </ParticularsModalContent>
        </ParticularsModalOverlay>
      )}

      {/* Particulars Delete Confirmation Modal */}
      {showDeleteParticularModal && (
        <ParticularsModalOverlay onClick={closeDeleteParticularModal}>
          <ParticularsModalContent onClick={(e) => e.stopPropagation()}>
            <ParticularsModalHeader style={{ background: '#dc3545', color: 'white' }}>
              <ParticularsModalTitle style={{ color: 'white' }}>Confirm Delete Particular</ParticularsModalTitle>
              <CloseButton onClick={closeDeleteParticularModal} style={{ color: 'white' }}>
                <FaTimes />
              </CloseButton>
            </ParticularsModalHeader>

            <ParticularsModalBody>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '1.2rem', marginBottom: '15px', color: '#dc3545' }}>
                  ⚠️ Warning: This action cannot be undone!
                </div>
                <p style={{ fontSize: '1rem', marginBottom: '20px', color: '#333' }}>
                  Are you sure you want to delete <strong>{selectedParticular}</strong> and all of its files?
                </p>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '30px' }}>
                  This will permanently remove every file for this particular from the system. This action cannot be undone.
                </p>
              </div>

              <ParticularsButtonGroup>
                <ParticularsCancelButton onClick={closeDeleteParticularModal}>
                  Cancel
                </ParticularsCancelButton>
                <ParticularsSubmitButton
                  onClick={handleParticularDeleteConfirm}
                  disabled={deletingParticular}
                  style={{ background: '#dc3545', borderColor: '#dc3545' }}
                >
                  {deletingParticular ? 'Deleting...' : 'Yes, Delete'}
                </ParticularsSubmitButton>
              </ParticularsButtonGroup>
            </ParticularsModalBody>
          </ParticularsModalContent>
        </ParticularsModalOverlay>
      )}

      {/* Document Upload Modal */}
      {showDocumentModal && (
        <ParticularsModalOverlay onClick={closeDocumentModal}>
          <ParticularsModalContent onClick={(e) => e.stopPropagation()}>
            <ParticularsModalHeader>
              <ParticularsModalTitle>
                Upload {selectedDocumentType === 'social_security_upload' ? 'SSN Document' :
                  selectedDocumentType === 'dl_upload' ? 'DL or State ID Document' :
                    selectedDocumentType === 'medical_evaluation_form' ? 'Medical Evaluation Form' :
                      'Document'}
              </ParticularsModalTitle>
              <CloseButton onClick={closeDocumentModal}>
                <FaTimes />
              </CloseButton>
            </ParticularsModalHeader>

            <ParticularsModalBody>
              <form onSubmit={handleDocumentSubmit}>
                <ParticularsFormGroup>
                  <ParticularsLabel>Select File</ParticularsLabel>
                  <ParticularsInput
                    type="file"
                    onChange={handleDocumentFileChange}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    required
                  />
                </ParticularsFormGroup>

                <ParticularsButtonGroup>
                  <ParticularsCancelButton type="button" onClick={closeDocumentModal}>
                    Cancel
                  </ParticularsCancelButton>
                  <ParticularsSubmitButton type="submit" disabled={uploadingDocument || !selectedDocumentFile}>
                    {uploadingDocument ? 'Uploading...' : 'Upload'}
                  </ParticularsSubmitButton>
                </ParticularsButtonGroup>
              </form>
            </ParticularsModalBody>
          </ParticularsModalContent>
        </ParticularsModalOverlay>
      )}

      {/* Update Confirmation Modal */}
      {showUpdateConfirm && (
        <ParticularsModalOverlay onClick={handleUpdateCancel}>
          <ParticularsModalContent onClick={(e) => e.stopPropagation()}>
            <ParticularsModalHeader style={{ background: 'rgb(51, 66, 147)', color: 'white' }}>
              <ParticularsModalTitle style={{ color: 'white' }}>Confirm Document Update</ParticularsModalTitle>
              <CloseButton onClick={handleUpdateCancel} style={{ color: '#000' }}>
                <FaTimes />
              </CloseButton>
            </ParticularsModalHeader>

            <ParticularsModalBody>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '1.2rem', marginBottom: '15px', color: '#856404' }}>
                  ⚠️ Warning: This will replace the existing file!
                </div>
                <p style={{ fontSize: '1rem', marginBottom: '20px', color: '#333' }}>
                  Are you sure you want to update the <strong>{pendingDocumentUpdate?.documentName}</strong>?
                </p>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '30px' }}>
                  This will permanently replace the current file with the new one. The old file will be deleted.
                </p>
              </div>

              <ParticularsButtonGroup>
                <ParticularsCancelButton onClick={handleUpdateCancel}>
                  Cancel
                </ParticularsCancelButton>
                <ParticularsSubmitButton
                  onClick={handleUpdateConfirm}
                  style={{ background: 'rgb(51, 66, 147)', color: 'white', borderColor: '#ffc107' }}
                >
                  Yes, Update File
                </ParticularsSubmitButton>
              </ParticularsButtonGroup>
            </ParticularsModalBody>
          </ParticularsModalContent>
        </ParticularsModalOverlay>
      )}

      {/* Signature Modal */}
      {showSignatureModal && (
        <ParticularsModalOverlay onClick={() => setShowSignatureModal(false)}>
          <ParticularsModalContent onClick={(e) => e.stopPropagation()}>
            <ParticularsModalHeader>
              <ParticularsModalTitle>Student Signature</ParticularsModalTitle>
              <CloseButton onClick={() => setShowSignatureModal(false)}>
                <FaTimes />
              </CloseButton>
            </ParticularsModalHeader>

            <ParticularsModalBody>
              <ParticularsFormGroup>
                <ParticularsLabel>Signature</ParticularsLabel>
                <SignaturePadContainer>
                  <SignatureCanvas
                    ref={signatureCanvasRef}
                    onMouseDown={startSignatureDrawing}
                    onMouseMove={drawSignature}
                    onMouseUp={stopSignatureDrawing}
                    onMouseLeave={stopSignatureDrawing}
                  />
                  <ClearSignatureButton type="button" onClick={clearSignature}>
                    Clear Signature
                  </ClearSignatureButton>
                </SignaturePadContainer>
              </ParticularsFormGroup>

              <ParticularsButtonGroup>
                <ParticularsCancelButton type="button" onClick={() => setShowSignatureModal(false)}>
                  Cancel
                </ParticularsCancelButton>
                <ParticularsSubmitButton 
                  type="button" 
                  onClick={handleSaveSignature}
                  disabled={savingSignature || !studentSignature}
                >
                  {savingSignature ? 'Saving...' : 'Save Signature'}
                </ParticularsSubmitButton>
              </ParticularsButtonGroup>
            </ParticularsModalBody>
          </ParticularsModalContent>
        </ParticularsModalOverlay>
      )}
    </ModalOverlay>
  );
}

// Component to handle profile image with fallback paths
const ProfileImageWithFallback = ({ student, urls, onError, onClick }) => {
  const [currentUrlIndex, setCurrentUrlIndex] = useState(0);
  const [hasError, setHasError] = useState(false);
  
  if (!urls) {
    return null;
  }
  
  const urlsToTry = [urls.primary, urls.fallback].filter(Boolean);
  
  if (urlsToTry.length === 0 || hasError) {
    return null;
  }
  
  const handleError = (e) => {
    if (currentUrlIndex < urlsToTry.length - 1) {
      // Try the next URL
      setCurrentUrlIndex(currentUrlIndex + 1);
    } else {
      // All URLs failed, show default avatar
      setHasError(true);
      e.target.style.display = 'none';
      if (onError) onError();
    }
  };
  
  const handleClick = () => {
    if (onClick && urlsToTry[currentUrlIndex]) {
      onClick(urlsToTry[currentUrlIndex]);
    }
  };
  
  return (
    <ProfileImage 
      key={currentUrlIndex} // Force re-render when URL changes
      src={urlsToTry[currentUrlIndex]} 
      alt={`${student.firstName} ${student.lastName}`}
      onError={handleError}
      onClick={handleClick}
    />
  );
};

/** Raw DOB from list API uses date_of_birth; some paths use dateOfBirth */
function getStudentDobDate(student) {
  const raw = student?.date_of_birth ?? student?.dateOfBirth;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function calculateAgeFromBirthDate(birthDate) {
  if (!birthDate) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age;
}

function getStudentAge(student) {
  const d = getStudentDobDate(student);
  return d != null ? calculateAgeFromBirthDate(d) : null;
}

function formatStudentAgeDisplay(student) {
  const a = getStudentAge(student);
  return a != null ? String(a) : '—';
}

function parseAgeBound(value) {
  const s = value === '' || value == null ? '' : String(value).trim();
  if (s === '') return { kind: 'empty' };
  const n = parseInt(s, 10);
  if (Number.isNaN(n) || n < 0 || n > 130) return { kind: 'invalid' };
  return { kind: 'ok', value: n };
}

/** Same values as registration form location preference selects */
const STUDENT_CAMPUS_OPTIONS = [
  'Main Campus Thorek',
  'Evergreen Campus',
];

const STUDENT_STATUS_OPTIONS = ['Active', 'Inactive', 'Graduated', 'Withdrawn'];

const LEGACY_STATUS_MAP = {
  approved: 'Active',
  pending: 'Inactive',
};

function normalizeStudentStatus(raw) {
  const key = String(raw ?? '').trim().toLowerCase();
  if (!key) return '';
  if (LEGACY_STATUS_MAP[key]) return LEGACY_STATUS_MAP[key].toLowerCase();
  const matched = STUDENT_STATUS_OPTIONS.find((s) => s.toLowerCase() === key);
  return matched ? matched.toLowerCase() : key;
}

function formatStudentStatusLabel(status) {
  const normalized = normalizeStudentStatus(status);
  if (!normalized) return 'N/A';
  const matched = STUDENT_STATUS_OPTIONS.find((s) => s.toLowerCase() === normalized);
  return matched || normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function studentMatchesStatus(student, filterStatus) {
  if (!filterStatus) return true;
  return normalizeStudentStatus(student?.status) === normalizeStudentStatus(filterStatus);
}

function studentMatchesNameSearch(student, searchTerm) {
  const trimmed = (searchTerm || '').trim();
  if (!trimmed) return true;

  const normalize = (s) =>
    String(s ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  const searchLower = normalize(trimmed);

  const first = normalize(student.firstName);
  const last = normalize(student.lastName);
  const fullForward = `${first} ${last}`.trim();
  const fullReverse = `${last} ${first}`.trim();
  const emailNorm = student.email ? normalize(student.email) : '';
  const regNorm = student.registration_number
    ? String(student.registration_number).toLowerCase()
    : '';

  return (
    (first && first.includes(searchLower)) ||
    (last && last.includes(searchLower)) ||
    (fullForward && fullForward.includes(searchLower)) ||
    (fullReverse && fullReverse.includes(searchLower)) ||
    (emailNorm && emailNorm.includes(searchLower)) ||
    (regNorm && regNorm.includes(searchLower))
  );
}

function getStudentCareerCoach(student) {
  return String(student?.workforceCenter ?? student?.workforce_center ?? '').trim();
}

function getStudentCampusDisplay(student) {
  const p1 = String(student?.locationPref1 ?? student?.location_pref1 ?? '').trim();
  const p2 = String(student?.locationPref2 ?? student?.location_pref2 ?? '').trim();
  if (p1 && p2 && p1 !== p2) return `${p1}; ${p2}`;
  return p1 || p2 || '';
}

function studentMatchesCampus(student, campus) {
  if (!campus) return true;
  const campusLower = String(campus).toLowerCase();
  const p1 = String(student?.locationPref1 ?? student?.location_pref1 ?? '').trim().toLowerCase();
  const p2 = String(student?.locationPref2 ?? student?.location_pref2 ?? '').trim().toLowerCase();
  return p1 === campusLower || p2 === campusLower;
}

function filterStudentsByCourseSessionCoach(studentList, { course, session, careerCoach, campus, status }, sessionsList, coachesList = []) {
  let filtered = studentList;

  if (course) {
    filtered = filtered.filter(
      (student) =>
        student.course === course ||
        student.course?.toLowerCase() === String(course).toLowerCase()
    );
  }

  if (session) {
    const selectedSession = sessionsList.find((s) => String(s.id) === String(session));
    filtered = filtered.filter((student) => {
      if (!student.session) return false;
      const studentSession = student.session;
      const sessionId = session;
      const sessionName = selectedSession?.session_name;
      if (String(studentSession) === String(sessionId)) return true;
      if (sessionName && String(studentSession) === String(sessionName)) return true;
      if (sessionName && studentSession?.toLowerCase() === sessionName.toLowerCase()) return true;
      if (studentSession && sessionId && studentSession.toString() === sessionId.toString()) return true;
      return false;
    });
  }

  if (careerCoach) {
    const coachLower = String(careerCoach).toLowerCase();
    const matchedCoach = coachesList.find((c) => c.name?.toLowerCase() === coachLower);
    filtered = filtered.filter((student) => {
      if (matchedCoach && student.careerCoachId != null) {
        return String(student.careerCoachId) === String(matchedCoach.id);
      }
      return getStudentCareerCoach(student).toLowerCase() === coachLower;
    });
  }

  if (campus) {
    filtered = filtered.filter((student) => studentMatchesCampus(student, campus));
  }

  if (status) {
    filtered = filtered.filter((student) => studentMatchesStatus(student, status));
  }

  return filtered;
}

function resolveCourseLabel(student, coursesList) {
  const match = coursesList.find(
    (c) =>
      String(c.id) === String(student.course) ||
      c.course_name === student.course ||
      c.course_name?.toLowerCase() === String(student.course || '').toLowerCase()
  );
  return match?.course_name || student.course || '';
}

function resolveSessionLabel(student, sessionsList) {
  const match = sessionsList.find(
    (s) =>
      String(s.id) === String(student.session) ||
      s.session_name === student.session ||
      s.session_name?.toLowerCase() === String(student.session || '').toLowerCase()
  );
  return match?.session_name || student.session || '';
}

const StudentList = () => {
  const dispatch = useDispatch();
  const { students, isLoading } = useSelector(state => state.masterData);
  const { courses, sessions } = useSelector(state => state.masterData);
  const { particulars } = useSelector(state => state.masterData);
  const { confirmState, showDeleteConfirm } = useConfirm();
  const [filters, setFilters] = useState({
    course: '',
    session: '',
    campus: '',
    status: '',
    ageFrom: '',
    ageTo: ''
  });
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedImageUrl, setExpandedImageUrl] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isUpdatingBulkStatus, setIsUpdatingBulkStatus] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    course: '',
    session: '',
    careerCoach: '',
    campus: '',
    status: ''
  });
  const [exportSelectedIds, setExportSelectedIds] = useState([]);
  const [updateStatusFilters, setUpdateStatusFilters] = useState({
    course: '',
    session: '',
    campus: '',
    search: ''
  });
  const [updateStatusSelectedIds, setUpdateStatusSelectedIds] = useState([]);
  const [bulkStatusValue, setBulkStatusValue] = useState('Active');
  const { token, user } = useSelector(state => state.auth);
  const canUpdateStudent = hasPermission(user, 'students_update');
  const canDeleteStudent = hasPermission(user, 'students_delete');

  // Path validator function to remove server path prefixes
  const validateFilePath = (filePath) => {
    if (!filePath) return '';

    // Remove the /home/cclcusa/public_html/ prefix if it exists
    const prefixToRemove1 = '/home/cclcusa/public_html/';
    if (filePath.startsWith(prefixToRemove1)) {
      const cleanedPath = filePath.substring(prefixToRemove1.length);
      return cleanedPath;
    }

    // Remove the /opt/render/project/src/ prefix if it exists
    const prefixToRemove2 = '/opt/render/project/src/';
    if (filePath.startsWith(prefixToRemove2)) {
      const cleanedPath = filePath.substring(prefixToRemove2.length);
      return cleanedPath;
    }

    // Remove the admin/backend/uploads/ prefix if it exists
    const prefixToRemove3 = 'admin/backend/uploads/';
    if (filePath.startsWith(prefixToRemove3)) {
      const cleanedPath = filePath.substring(prefixToRemove3.length);
      return cleanedPath;
    }

    // If the path contains 'uploads/', extract only the part after it
    if (filePath.includes('uploads/')) {
      const uploadsIndex = filePath.indexOf('uploads/');
      return filePath.substring(uploadsIndex + 'uploads/'.length);
    }

    return filePath;
  };

  // Function to build the complete file URL
  const buildFileUrl = (filePath) => {
    if (!filePath) return '';

    const validatedPath = validateFilePath(filePath);

    // Use secure file access for all files
    const secureUrl = getSecureFileUrlWithToken(validatedPath, token);

    return secureUrl;
  };

  // Helper function to get profile image URLs (tries both possible locations)
  const getProfileImageUrls = (student) => {
    if (!student.photo || student.photo === 'N/A' || student.photo === 'null' || !student.email) {
      return null;
    }

    // Create safe email (replace @ and . with _)
    const safeEmail = student.email.replace(/[@.]/g, '_');
    
    // Clean the photo path first (remove server path prefixes)
    let photoPath = validateFilePath(student.photo);
    
    // Extract filename from photo path
    // Photo path might be: "profile.jpg", "students/{email}/profile.jpg", or full path
    let filename = photoPath;
    
    // Check if path already contains the student folder structure
    const studentFolderPattern = new RegExp(`students/${safeEmail}(/documents)?/`);
    if (studentFolderPattern.test(photoPath)) {
      // Path already contains the folder structure, extract just the filename
      filename = photoPath.split('/').pop();
    } else if (photoPath.includes('/')) {
      // Path contains slashes but not the student folder, extract filename
      filename = photoPath.split('/').pop();
    }
    
    // Ensure filename starts with "profile" and has an extension
    if (!filename.startsWith('profile')) {
      // If it's not "profile", try to extract extension or use default
      const ext = filename.includes('.') ? filename.split('.').pop() : 
                  (photoPath.includes('.') ? photoPath.split('.').pop() : 'jpg');
      filename = `profile.${ext}`;
    }
    
    // Build both possible paths (try without documents first, then with documents)
    const path1 = `students/${safeEmail}/${filename}`;
    const path2 = `students/${safeEmail}/documents/${filename}`;
    
    return {
      primary: buildFileUrl(path1),
      fallback: buildFileUrl(path2)
    };
  };

  // No need to fetch students, use persisted masterData

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value
    });
  };

  // Auto-search when filters change
  useEffect(() => {
    handleSearch();
  }, [filters]);

  const handleSearch = () => {
    const fromP = parseAgeBound(filters.ageFrom);
    const toP = parseAgeBound(filters.ageTo);
    const ageInputInvalid = fromP.kind === 'invalid' || toP.kind === 'invalid';
    const ageFromOk = fromP.kind === 'ok' ? fromP.value : null;
    const ageToOk = toP.kind === 'ok' ? toP.value : null;
    const hasAnyAgeBound = ageFromOk !== null || ageToOk !== null;
    const rangeOrderInvalid =
      ageFromOk !== null && ageToOk !== null && ageFromOk > ageToOk;
    const hasAgeFilter = hasAnyAgeBound && !ageInputInvalid && !rangeOrderInvalid;

    // If no filters are selected, show all students
    if (!filters.course && !filters.session && !filters.campus && !filters.status && !hasAgeFilter) {
      if (ageInputInvalid || rangeOrderInvalid) {
        setFilteredStudents([]);
        setIsFiltered(true);
        return;
      }
      setFilteredStudents([]);
      setIsFiltered(false);
      return;
    }

    let filtered = students;

    if (filters.status) {
      filtered = filtered.filter((student) => studentMatchesStatus(student, filters.status));
    }

    if (filters.course) {
      filtered = filtered.filter(student =>
        student.course === filters.course ||
        student.course?.toLowerCase() === filters.course.toLowerCase()
      );
    }

    if (filters.campus) {
      filtered = filtered.filter((student) => studentMatchesCampus(student, filters.campus));
    }

    if (filters.session) {
      // Find the session object for the selected session ID
      const selectedSession = sessions.find(s => String(s.id) === String(filters.session));

      filtered = filtered.filter(student => {
        // If student has no session data, skip them
        if (!student.session) {
          return false;
        }

        // Try multiple matching strategies
        const studentSession = student.session;
        const sessionId = filters.session;
        const sessionName = selectedSession?.session_name;

        // Match by session ID
        if (String(studentSession) === String(sessionId)) {
          return true;
        }

        // Match by session name
        if (sessionName && String(studentSession) === String(sessionName)) {
          return true;
        }

        // Match by session name (case insensitive)
        if (sessionName && studentSession?.toLowerCase() === sessionName.toLowerCase()) {
          return true;
        }

        // Match by session ID as string comparison
        if (studentSession && sessionId && studentSession.toString() === sessionId.toString()) {
          return true;
        }

        return false;
      });
    }

    if (hasAgeFilter) {
      filtered = filtered.filter((student) => {
        const age = getStudentAge(student);
        if (age === null) return false;
        if (ageFromOk !== null && age < ageFromOk) return false;
        if (ageToOk !== null && age > ageToOk) return false;
        return true;
      });
    } else if ((ageInputInvalid || rangeOrderInvalid) && (filters.course || filters.session || filters.campus || filters.status)) {
      filtered = [];
    }

    setFilteredStudents(filtered);
    setIsFiltered(true);
  };

  const handleClearFilters = () => {
    setFilters({
      course: '',
      session: '',
      campus: '',
      status: '',
      ageFrom: '',
      ageTo: ''
    });
    setFilteredStudents([]);
    setIsFiltered(false);
  };

  // Get the students to display (filtered or all)
  const displayStudents = isFiltered ? filteredStudents : students;

  // Text search functionality (supports full name: "first last" or "last first")
  const textFilteredStudents = displayStudents.filter((student) => {
    const trimmed = (searchTerm || '').trim();
    if (!trimmed) return true;

    const normalize = (s) =>
      String(s ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
    const searchLower = normalize(trimmed);

    const first = normalize(student.firstName);
    const last = normalize(student.lastName);
    const fullForward = `${first} ${last}`.trim();
    const fullReverse = `${last} ${first}`.trim();

    const emailNorm = student.email ? normalize(student.email) : '';
    const regNorm = student.registration_number
      ? String(student.registration_number).toLowerCase()
      : '';

    return (
      (first && first.includes(searchLower)) ||
      (last && last.includes(searchLower)) ||
      (fullForward && fullForward.includes(searchLower)) ||
      (fullReverse && fullReverse.includes(searchLower)) ||
      (emailNorm && emailNorm.includes(searchLower)) ||
      (regNorm && regNorm.includes(searchLower))
    );
  }).sort((a, b) => {
    // Sort by last name first, then by first name
    const aLastName = (a.lastName || '').toLowerCase();
    const bLastName = (b.lastName || '').toLowerCase();
    const aFirstName = (a.firstName || '').toLowerCase();
    const bFirstName = (b.firstName || '').toLowerCase();

    if (aLastName !== bLastName) {
      return aLastName.localeCompare(bLastName);
    }
    return aFirstName.localeCompare(bFirstName);
  });

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };



  const handleSend = (studentId) => {
    // TODO: Implement send functionality
  };

  const handleDownload = (studentId) => {
    // TODO: Implement download functionality
  };

  const handleDelete = async (studentId) => {
    const confirmed = await showDeleteConfirm('student', 'student');
    if (!confirmed) return;

    try {
      await deleteStudent(studentId);
      // Update Redux state to remove the deleted student
      dispatch({
        type: 'masterData/removeStudent',
        payload: studentId
      });
      showSuccess('Student deleted successfully');
    } catch (err) {
      console.error('Error deleting student:', err);
      showError('Error deleting student');
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      // Dispatch the fetchMasterData async thunk to fetch fresh data from API
      await dispatch(fetchMasterData()).unwrap();
      showSuccess('Students list refreshed successfully!');
    } catch (error) {
      console.error('Error refreshing students:', error);
      showError('Failed to refresh students list');
    } finally {
      setRefreshing(false);
    }
  };

  const [careerCoachFilterOptions, setCareerCoachFilterOptions] = useState([]);

  useEffect(() => {
    fetchCareerCoaches()
      .then(setCareerCoachFilterOptions)
      .catch(() => setCareerCoachFilterOptions([]));
  }, []);

  const exportFilteredStudents = useMemo(() => {
    const filtered = filterStudentsByCourseSessionCoach(
      students,
      exportFilters,
      sessions,
      careerCoachFilterOptions
    );
    return [...filtered].sort((a, b) => {
      const aLast = (a.lastName || '').toLowerCase();
      const bLast = (b.lastName || '').toLowerCase();
      if (aLast !== bLast) return aLast.localeCompare(bLast);
      return (a.firstName || '').toLowerCase().localeCompare((b.firstName || '').toLowerCase());
    });
  }, [students, exportFilters, sessions, careerCoachFilterOptions]);

  const exportFilteredStudentIdsKey = useMemo(
    () => exportFilteredStudents.map((s) => s.id).join(','),
    [exportFilteredStudents]
  );

  useEffect(() => {
    if (!showExportModal) return;
    setExportSelectedIds(exportFilteredStudents.map((s) => s.id));
  }, [exportFilteredStudentIdsKey, showExportModal]);

  const exportSelectedStudents = useMemo(
    () => exportFilteredStudents.filter((s) => exportSelectedIds.includes(s.id)),
    [exportFilteredStudents, exportSelectedIds]
  );

  const isExportStudentSelected = (studentId) => exportSelectedIds.includes(studentId);

  const toggleExportStudentSelection = (studentId) => {
    setExportSelectedIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const selectAllExportStudents = () => {
    setExportSelectedIds(exportFilteredStudents.map((s) => s.id));
  };

  const deselectAllExportStudents = () => {
    setExportSelectedIds([]);
  };

  const openExportModal = () => {
    setExportFilters({ course: '', session: '', careerCoach: '', campus: '', status: '' });
    setExportSelectedIds([]);
    setShowExportModal(true);
  };

  const closeExportModal = () => {
    setShowExportModal(false);
    setExportFilters({ course: '', session: '', careerCoach: '', campus: '', status: '' });
    setExportSelectedIds([]);
  };

  const updateStatusFilteredStudents = useMemo(() => {
    const filtered = filterStudentsByCourseSessionCoach(
      students,
      updateStatusFilters,
      sessions,
      []
    ).filter((student) => studentMatchesNameSearch(student, updateStatusFilters.search));
    return [...filtered].sort((a, b) => {
      const aLast = (a.lastName || '').toLowerCase();
      const bLast = (b.lastName || '').toLowerCase();
      if (aLast !== bLast) return aLast.localeCompare(bLast);
      return (a.firstName || '').toLowerCase().localeCompare((b.firstName || '').toLowerCase());
    });
  }, [students, updateStatusFilters, sessions]);

  const updateStatusFilteredStudentIdsKey = useMemo(
    () => updateStatusFilteredStudents.map((s) => s.id).join(','),
    [updateStatusFilteredStudents]
  );

  useEffect(() => {
    if (!showUpdateStatusModal) return;
    setUpdateStatusSelectedIds(updateStatusFilteredStudents.map((s) => s.id));
  }, [updateStatusFilteredStudentIdsKey, showUpdateStatusModal]);

  const isUpdateStatusStudentSelected = (studentId) => updateStatusSelectedIds.includes(studentId);

  const toggleUpdateStatusStudentSelection = (studentId) => {
    setUpdateStatusSelectedIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const selectAllUpdateStatusStudents = () => {
    setUpdateStatusSelectedIds(updateStatusFilteredStudents.map((s) => s.id));
  };

  const deselectAllUpdateStatusStudents = () => {
    setUpdateStatusSelectedIds([]);
  };

  const openUpdateStatusModal = () => {
    setUpdateStatusFilters({ course: '', session: '', campus: '', search: '' });
    setUpdateStatusSelectedIds([]);
    setBulkStatusValue('Active');
    setShowUpdateStatusModal(true);
  };

  const closeUpdateStatusModal = () => {
    setShowUpdateStatusModal(false);
    setUpdateStatusFilters({ course: '', session: '', campus: '', search: '' });
    setUpdateStatusSelectedIds([]);
    setBulkStatusValue('Active');
  };

  const handleUpdateStatusFilterChange = (field, value) => {
    setUpdateStatusFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'course') next.session = '';
      return next;
    });
  };

  const handleBulkStatusUpdate = async () => {
    if (updateStatusFilteredStudents.length === 0) {
      showError('No students match the selected filters');
      return;
    }
    if (updateStatusSelectedIds.length === 0) {
      showError('Select at least one student to update');
      return;
    }
    if (!bulkStatusValue) {
      showError('Select a status to apply');
      return;
    }

    try {
      setIsUpdatingBulkStatus(true);
      const result = await bulkUpdateStudentStatus(updateStatusSelectedIds, bulkStatusValue);

      updateStatusSelectedIds.forEach((studentId) => {
        dispatch(updateStudentAction({ studentId, updatedData: { status: bulkStatusValue } }));
      });

      showSuccess(result.message || `Updated status to ${bulkStatusValue} for ${updateStatusSelectedIds.length} student(s)`);
      closeUpdateStatusModal();
    } catch (error) {
      console.error('Error updating student status:', error);
      showError(error.message || 'Failed to update student status');
    } finally {
      setIsUpdatingBulkStatus(false);
    }
  };

  const handleExportFilterChange = (field, value) => {
    setExportFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'course') next.session = '';
      return next;
    });
  };

  const handleExportStudents = () => {
    if (exportFilteredStudents.length === 0) {
      showError('No students match the selected filters');
      return;
    }
    if (exportSelectedStudents.length === 0) {
      showError('Select at least one student to export');
      return;
    }
    try {
      setIsExporting(true);
      const excelData = exportSelectedStudents.map((student) => ({
        ID: student.id ?? '',
        'Last Name': student.lastName || '',
        'First Name': student.firstName || '',
        'Email Address': student.email || '',
        Gender: student.gender || '',
        Age: formatStudentAgeDisplay(student) === '—' ? '' : formatStudentAgeDisplay(student),
        'Phone Number': student.phone || '',
        'Enrollment Date': student.enrollmentDate || '',
        Status: formatStudentStatusLabel(student.status),
        Course: resolveCourseLabel(student, courses),
        Session: resolveSessionLabel(student, sessions),
        Campus: getStudentCampusDisplay(student),
        'Career Coach': getStudentCareerCoach(student)
      }));
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');
      const timestamp = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `student_list_export_${timestamp}.xlsx`);
      showSuccess(`Exported ${exportSelectedStudents.length} student(s) to Excel`);
      closeExportModal();
    } catch (error) {
      console.error('Error exporting students:', error);
      showError('Failed to export student list. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleUpdateStudent = async (studentId, updatedData) => {
    try {
      // Call the update API
      const response = await fetch(`${API_BASE_URL}/students/${studentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) {
        throw new Error('Failed to update student');
      }

      // Update the student in Redux state immediately
      dispatch(updateStudentAction({ studentId, updatedData }));

      showSuccess('Student updated successfully!');
    } catch (error) {
      console.error('Error updating student:', error);
      showError('Failed to update student');
      throw error;
    }
  };

  return (
    <Container>
      <GlobalStyle />


      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <ListTitle>
          Student List
          {isFiltered && (
            <span style={{ fontSize: '0.9rem', color: '#6c757d', fontWeight: 'normal', marginLeft: '10px' }}>
              ({displayStudents.length} filtered results)
            </span>
          )}
        </ListTitle>
        <HeaderActions>
          {canUpdateStudent && (
            <UpdateStatusButton type="button" onClick={openUpdateStatusModal} disabled={isLoading}>
              <FaPencilAlt />
              Update Student Status
            </UpdateStatusButton>
          )}
          <ExportButton type="button" onClick={openExportModal} disabled={isLoading}>
            <FaDownload />
            Export Student List
          </ExportButton>
          <RefreshButton
            onClick={handleRefresh}
            disabled={refreshing || isLoading}
          >
            <FaSync style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </RefreshButton>
        </HeaderActions>
      </div>

      <FilterSection>
        <FilterRow>
          <FilterGroup>
            <Label htmlFor="name">Search by name</Label>
            <SearchInput
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </FilterGroup>

          <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
            <FilterGroup>
              <Label htmlFor="course">Course *</Label>
              <CourseSelect
                value={filters.course}
                onChange={(value) => handleFilterChange('course', value)}
                placeholder="Select course"
                isClearable={true}
                style={{
                  padding: '11px'
                }}
              />
            </FilterGroup>

            <FilterGroup>
              <Label htmlFor="session">Session *</Label>
              <SessionSelect
                value={filters.session}
                onChange={(value) => handleFilterChange('session', value)}
                placeholder="Select session"
                isClearable={true}
                courseId={filters.course}
                sessions={sessions}
                style={{
                  padding: '11px'
                }}
              />
            </FilterGroup>

            <FilterGroup>
              <Label htmlFor="campus">Campus</Label>
              <Select
                id="campus"
                value={filters.campus}
                onChange={(e) => handleFilterChange('campus', e.target.value)}
              >
                <option value="">All campuses</option>
                {STUDENT_CAMPUS_OPTIONS.map((campus) => (
                  <option key={campus} value={campus}>
                    {campus}
                  </option>
                ))}
              </Select>
            </FilterGroup>

            <FilterGroup>
              <Label htmlFor="student-status">Status</Label>
              <Select
                id="student-status"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All statuses</option>
                {STUDENT_STATUS_OPTIONS.map((statusOption) => (
                  <option key={statusOption} value={statusOption}>
                    {statusOption}
                  </option>
                ))}
              </Select>
            </FilterGroup>

            <FilterGroup style={{ minWidth: '88px', maxWidth: '100px' }}>
              <Label htmlFor="student-age-from">From age</Label>
              <SearchInput
                id="student-age-from"
                type="number"
                inputMode="numeric"
                min={0}
                max={130}
                placeholder="Min"
                value={filters.ageFrom}
                onChange={(e) => handleFilterChange('ageFrom', e.target.value)}
              />
            </FilterGroup>
            <FilterGroup style={{ minWidth: '88px', maxWidth: '100px' }}>
              <Label htmlFor="student-age-to">To age</Label>
              <SearchInput
                id="student-age-to"
                type="number"
                inputMode="numeric"
                min={0}
                max={130}
                placeholder="Max"
                value={filters.ageTo}
                onChange={(e) => handleFilterChange('ageTo', e.target.value)}
              />
            </FilterGroup>

            <SearchButton onClick={handleSearch} style={{ padding: '14px' }}>
              <FaSearch />
              Search
            </SearchButton>

            {isFiltered && (
              <SearchButton
                onClick={handleClearFilters}
                style={{ marginLeft: '10px' }}
              >
                <FaTimes />
                Clear
              </SearchButton>
            )}
          </div>
        </FilterRow>
      </FilterSection>

      <StudentListContainer>
        <ListHeader>
          {/* <BreadcrumbNav>
            <BreadcrumbItem>Active</BreadcrumbItem>
            <BreadcrumbItem>EKG</BreadcrumbItem>
            <BreadcrumbItem>Saturday EKG Dr. Fernando II Soto Avellanez 08/24/2023 @ Evergreen</BreadcrumbItem>
          </BreadcrumbNav> */}

        </ListHeader>

       

        <Table style={{ display: 'inline-table' }}>
          <Thead>
            <Tr>
              <Th style={{ width: '50px' }}>Details</Th>
              <SortableHeader>ID</SortableHeader>
              <SortableHeader>Last Name</SortableHeader>
              <SortableHeader>First Name</SortableHeader>
              <SortableHeader>Email Address</SortableHeader>
              <SortableHeader>Gender</SortableHeader>
              <SortableHeader>Age</SortableHeader>
              <SortableHeader>Phone Number</SortableHeader>
              <SortableHeader>Enrollment Date</SortableHeader>
              <SortableHeader>Status</SortableHeader>
              <SortableHeader style={{ textAlign: 'left' }}>Actions</SortableHeader>
            </Tr>
          </Thead>
          <Tbody>
            {textFilteredStudents.length > 0 ? (
              textFilteredStudents.map((student) => (
                <React.Fragment key={student.id}>
                  <Tr>
                    <Td>
                      <ProfileImageContainer>
                        {(() => {
                          const imageUrls = getProfileImageUrls(student);
                          return (
                            <>
                              {imageUrls ? (
                                <ProfileImageWithFallback
                                  student={student}
                                  urls={imageUrls}
                                  onError={() => {
                                    const avatar = document.querySelector(`[data-avatar-id="${student.id}"]`);
                                    if (avatar) avatar.style.display = 'flex';
                                  }}
                                  onClick={(imageUrl) => setExpandedImageUrl(imageUrl)}
                                />
                              ) : null}
                              <DefaultAvatar 
                                data-avatar
                                data-avatar-id={student.id}
                                style={{ display: imageUrls ? 'none' : 'flex' }}
                              >
                                {student.firstName?.[0]?.toUpperCase() || ''}{student.lastName?.[0]?.toUpperCase() || ''}
                              </DefaultAvatar>
                              <ViewDetailsButton onClick={() => { setSelectedStudent(student); setShowDetailsModal(true); }}>
                                <FaRegIdCard />
                              </ViewDetailsButton>
                            </>
                          );
                        })()}
                      </ProfileImageContainer>
                    </Td>
                    <Td style={{ fontWeight: '500' }}>{student.id}</Td>
                    <Td style={{ fontWeight: '500' }}>{student.lastName}</Td>
                    <Td style={{ fontWeight: '500' }}>{student.firstName}</Td>
                    <Td style={{ color: 'rgb(51, 66, 147)' }}>{student.email}</Td>
                    <Td>{student.gender}</Td>
                    <Td>{formatStudentAgeDisplay(student)}</Td>
                    <Td style={{ fontFamily: 'Courier New, monospace' }}>
                      {student.phone}
                    </Td>
                    <Td style={{ fontSize: '0.85rem' }}>{student.enrollmentDate}</Td>
                    <Td>
                      <StudentStatusBadge status={student.status}>
                        {formatStudentStatusLabel(student.status)}
                      </StudentStatusBadge>
                    </Td>
                    <Td>
                      {(canUpdateStudent || canDeleteStudent) && (
                        <ActionButton
                          action="edit"
                          onClick={() => { setSelectedStudent(student); setShowEditModal(true); }}
                          style={{ borderColor: '#28a745', color: '#28a745' }}
                        >
                          <FaRegEdit />
                        </ActionButton>
                      )}
                    </Td>
                  </Tr>
                </React.Fragment>
              ))
            ) : (
              <Tr>
                <Td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                  {isFiltered ? 'No students found matching the selected criteria' : (searchTerm ? 'No students found matching the search term' : 'No students available')}
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>

        {/* Pagination goes here */}

        {/* 
        <Pagination>
          <ShowingText>Showing 763 of 763</ShowingText>
          <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
            Page navigation controls would go here
          </div>
        </Pagination> */}
      </StudentListContainer>
      {showDetailsModal && (
        <StudentDetailsModal
          student={selectedStudent}
          onClose={() => setShowDetailsModal(false)}
          buildFileUrl={buildFileUrl}
          canUpdateStudent={canUpdateStudent}
          onCareerCoachAssigned={(studentId, data) => {
            dispatch(
              updateStudentAction({
                studentId,
                updatedData: {
                  careerCoachId: data.careerCoachId,
                  workforceCenter: data.workforceCenter
                }
              })
            );
          }}
        />
      )}

      {showEditModal && selectedStudent && (
        <StudentEditModal
          student={selectedStudent}
          onClose={() => setShowEditModal(false)}
          onUpdate={handleUpdateStudent}
          canUpdateStudent={canUpdateStudent}
          canDeleteStudent={canDeleteStudent}
        />
      )}

      {/* Image Expansion Modal */}
      {expandedImageUrl && (
        <ImageModalOverlay 
          onClick={() => setExpandedImageUrl(null)}
        >
          <ImageModalClose onClick={() => setExpandedImageUrl(null)}>
            <FaTimes />
          </ImageModalClose>
          <ExpandedImage 
            src={expandedImageUrl} 
            alt="Expanded profile image"
            onClick={(e) => e.stopPropagation()}
          />
        </ImageModalOverlay>
      )}

      {showUpdateStatusModal && (
        <ExportModalOverlay onClick={closeUpdateStatusModal}>
          <ExportModalContent onClick={(e) => e.stopPropagation()}>
            <ExportModalTitle>Update Student Status</ExportModalTitle>
            <ExportModalSubtitle>
              Filter and select students, then choose the new status to apply.
            </ExportModalSubtitle>

            <ExportModalField>
              <Label htmlFor="update-status-search">Search by name</Label>
              <SearchInput
                id="update-status-search"
                type="text"
                placeholder="Search students..."
                value={updateStatusFilters.search}
                onChange={(e) => handleUpdateStatusFilterChange('search', e.target.value)}
                style={{marginLeft: '10px'}}
              />
            </ExportModalField>

            <ExportModalField>
              <Label htmlFor="update-status-course">Course</Label>
              <CourseSelect
                id="update-status-course"
                value={updateStatusFilters.course}
                onChange={(value) => handleUpdateStatusFilterChange('course', value)}
                placeholder="All courses"
                isClearable={true}
                style={{ padding: '11px' }}
              />
            </ExportModalField>

            <ExportModalField>
              <Label htmlFor="update-status-session">Session</Label>
              <SessionSelect
                id="update-status-session"
                value={updateStatusFilters.session}
                onChange={(value) => handleUpdateStatusFilterChange('session', value)}
                placeholder="All sessions"
                isClearable={true}
                courseId={updateStatusFilters.course}
                sessions={sessions}
                style={{ padding: '11px' }}
              />
            </ExportModalField>

            <ExportModalField>
              <Label htmlFor="update-status-campus">Campus</Label>
              <Select
                id="update-status-campus"
                value={updateStatusFilters.campus}
                onChange={(e) => handleUpdateStatusFilterChange('campus', e.target.value)}
                style={{marginLeft: '10px'}}
              >
                <option value="">All campuses</option>
                {STUDENT_CAMPUS_OPTIONS.map((campus) => (
                  <option key={campus} value={campus}>
                    {campus}
                  </option>
                ))}
              </Select>
            </ExportModalField>

            <ExportModalField>
              <Label htmlFor="update-status-new-status">New status</Label>
              <Select
                id="update-status-new-status"
                value={bulkStatusValue}
                onChange={(e) => setBulkStatusValue(e.target.value)}
                style={{marginLeft: '10px'}}
              >
                {STUDENT_STATUS_OPTIONS.map((statusOption) => (
                  <option key={statusOption} value={statusOption}>
                    {statusOption}
                  </option>
                ))}
              </Select>
            </ExportModalField>

            <ExportStudentListSection>
              <ExportStudentListHeader>
                <ExportStudentListSummary>
                  {updateStatusSelectedIds.length} of {updateStatusFilteredStudents.length} selected
                </ExportStudentListSummary>
                <ExportStudentListActions>
                  <ExportStudentListActionLink
                    type="button"
                    onClick={selectAllUpdateStatusStudents}
                    disabled={updateStatusFilteredStudents.length === 0}
                  >
                    Select all
                  </ExportStudentListActionLink>
                  <ExportStudentListActionLink
                    type="button"
                    onClick={deselectAllUpdateStatusStudents}
                    disabled={updateStatusFilteredStudents.length === 0}
                  >
                    Deselect all
                  </ExportStudentListActionLink>
                </ExportStudentListActions>
              </ExportStudentListHeader>

              {updateStatusFilteredStudents.length === 0 ? (
                <ExportStudentListEmpty>No students match the selected filters.</ExportStudentListEmpty>
              ) : (
                <ExportStudentList>
                  {updateStatusFilteredStudents.map((student) => (
                    <ExportStudentListItem key={student.id}>
                      <input
                        type="checkbox"
                        checked={isUpdateStatusStudentSelected(student.id)}
                        onChange={() => toggleUpdateStatusStudentSelection(student.id)}
                      />
                      <span>
                        {student.lastName || ''}
                        {student.lastName && student.firstName ? ', ' : ''}
                        {student.firstName || ''}
                      </span>
                      <ExportStudentListMeta>
                        ID {student.id} · {formatStudentStatusLabel(student.status)}
                      </ExportStudentListMeta>
                    </ExportStudentListItem>
                  ))}
                </ExportStudentList>
              )}
            </ExportStudentListSection>

            <ExportModalFooter>
              <ExportModalCancelButton type="button" onClick={closeUpdateStatusModal}>
                Cancel
              </ExportModalCancelButton>
              <ExportModalSubmitButton
                type="button"
                onClick={handleBulkStatusUpdate}
                disabled={
                  isUpdatingBulkStatus ||
                  updateStatusFilteredStudents.length === 0 ||
                  updateStatusSelectedIds.length === 0
                }
              >
                <FaPencilAlt />
                {isUpdatingBulkStatus ? 'Updating...' : 'Update Status'}
              </ExportModalSubmitButton>
            </ExportModalFooter>
          </ExportModalContent>
        </ExportModalOverlay>
      )}

      {showExportModal && (
        <ExportModalOverlay onClick={closeExportModal}>
          <ExportModalContent onClick={(e) => e.stopPropagation()}>
            <ExportModalTitle>Export Student List</ExportModalTitle>
            <ExportModalSubtitle>
              Optional filters — leave blank to include all students for that field.
            </ExportModalSubtitle>

            <ExportModalField>
              <Label htmlFor="export-course">Course</Label>
              <CourseSelect
                id="export-course"
                value={exportFilters.course}
                onChange={(value) => handleExportFilterChange('course', value)}
                placeholder="All courses"
                isClearable={true}
                style={{ padding: '11px' }}
              />
            </ExportModalField>

            <ExportModalField>
              <Label htmlFor="export-session">Session</Label>
              <SessionSelect
                id="export-session"
                value={exportFilters.session}
                onChange={(value) => handleExportFilterChange('session', value)}
                placeholder="All sessions"
                isClearable={true}
                courseId={exportFilters.course}
                sessions={sessions}
                style={{ padding: '11px' }}
              />
            </ExportModalField>

            <ExportModalField>
              <Label htmlFor="export-career-coach">Career Coach</Label>
              <Select
                id="export-career-coach"
                value={exportFilters.careerCoach}
                onChange={(e) => handleExportFilterChange('careerCoach', e.target.value)}
              >
                <option value="">All career coaches</option>
                {careerCoachFilterOptions.map((coach) => (
                  <option key={coach.id} value={coach.name}>
                    {coach.name}
                  </option>
                ))}
              </Select>
            </ExportModalField>

            <ExportModalField>
              <Label htmlFor="export-campus">Campus</Label>
              <Select
                id="export-campus"
                value={exportFilters.campus}
                onChange={(e) => handleExportFilterChange('campus', e.target.value)}
                style={{marginLeft: '10px'}}
              >
                <option value="">All campuses</option>
                {STUDENT_CAMPUS_OPTIONS.map((campus) => (
                  <option key={campus} value={campus}>
                    {campus}
                  </option>
                ))}
              </Select>
            </ExportModalField>

            <ExportModalField>
              <Label htmlFor="export-status">Status</Label>
              <Select
                id="export-status"
                value={exportFilters.status}
                onChange={(e) => handleExportFilterChange('status', e.target.value)}
                style={{marginLeft: '10px'}}
              >
                <option value="">All statuses</option>
                {STUDENT_STATUS_OPTIONS.map((statusOption) => (
                  <option key={statusOption} value={statusOption}>
                    {statusOption}
                  </option>
                ))}
              </Select>
            </ExportModalField>

            <ExportStudentListSection>
              <ExportStudentListHeader>
                <ExportStudentListSummary>
                  {exportSelectedIds.length} of {exportFilteredStudents.length} selected for export
                </ExportStudentListSummary>
                <ExportStudentListActions>
                  <ExportStudentListActionLink
                    type="button"
                    onClick={selectAllExportStudents}
                    disabled={exportFilteredStudents.length === 0}
                  >
                    Select all
                  </ExportStudentListActionLink>
                  <ExportStudentListActionLink
                    type="button"
                    onClick={deselectAllExportStudents}
                    disabled={exportFilteredStudents.length === 0}
                  >
                    Deselect all
                  </ExportStudentListActionLink>
                </ExportStudentListActions>
              </ExportStudentListHeader>

              {exportFilteredStudents.length === 0 ? (
                <ExportStudentListEmpty>No students match the selected filters.</ExportStudentListEmpty>
              ) : (
                <ExportStudentList>
                  {exportFilteredStudents.map((student) => (
                    <ExportStudentListItem key={student.id}>
                      <input
                        type="checkbox"
                        checked={isExportStudentSelected(student.id)}
                        onChange={() => toggleExportStudentSelection(student.id)}
                      />
                      <span>
                        {student.lastName || ''}
                        {student.lastName && student.firstName ? ', ' : ''}
                        {student.firstName || ''}
                      </span>
                      <ExportStudentListMeta>ID {student.id}</ExportStudentListMeta>
                    </ExportStudentListItem>
                  ))}
                </ExportStudentList>
              )}
            </ExportStudentListSection>

            <ExportModalFooter>
              <ExportModalCancelButton type="button" onClick={closeExportModal}>
                Cancel
              </ExportModalCancelButton>
              <ExportModalSubmitButton
                type="button"
                onClick={handleExportStudents}
                disabled={
                  isExporting ||
                  exportFilteredStudents.length === 0 ||
                  exportSelectedIds.length === 0
                }
              >
                <FaDownload />
                {isExporting ? 'Exporting...' : 'Export to Excel'}
              </ExportModalSubmitButton>
            </ExportModalFooter>
          </ExportModalContent>
        </ExportModalOverlay>
      )}

      <CustomConfirm
        isOpen={confirmState.isOpen}
        onClose={confirmState.onCancel}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        details={confirmState.details}
        itemName={confirmState.itemName}
      />
    </Container>
  );
};

export default StudentList; 